// ── Game ── Top-level game class and state machine
// Coordinates all subsystems: renderer, input, networking, gameplay, audio, HUD
// Method groups are extracted into game-init.ts, game-input-handlers.ts, game-network-handlers.ts

import * as THREE from 'three';
import { GameLoop } from './core/loop';
import { GameClock } from './core/clock';
import { input, encodeKeysFromState, encodeActionsFromState, type InputState } from './core/input';
import { TouchInputProvider } from './input/touch-input-provider';
import { keybindManager } from './core/keybinds';
import { TICK_RATE, WEAPONS, GRENADES, PLAYER_HEIGHT, PLAYER_CROUCH_HEIGHT, PLAYER_RADIUS, type WeaponId } from './core/config';
import { createScene, disposeScene, initCSM, updateCSM } from './renderer/scene';
import { FPSCamera } from './renderer/camera';
import { WeaponViewmodel } from './renderer/viewmodel';
import {
  spawnTracer, spawnBloodEffect,
  triggerScreenShake, applyScreenShake, undoScreenShake,
  disposeBloodPool, disposeTracerPool,
} from './renderer/effects';
import { initPools, disposePools } from './renderer/pool';
import { LocalPlayer, RemotePlayer } from './gameplay/player';
import {
  defaultLoadoutForTeam,
  nextWeaponInCycle,
  resetLoadoutForTeam,
  type LoadoutSlots,
} from './gameplay/loadout';
import { applyInput, isMoving, getSmoothedEyeHeight } from './gameplay/player-controller';
import {
  createWeaponState, canFire, fire, startReload, updateReload,
  getCurrentSpread, updateSpread, performHitscan,
  getRecoilKick,
  type WeaponState,
} from './gameplay/weapon';
import { Connection, type ServerMessage } from './net/connection';
import { type InputMessage, DeltaDecoder, isBinarySnapshot, decodeBinarySnapshot, type ExtendedSnapshot } from './net/messages';
import { PredictionSystem, type ApplyInputFn, type PlayerState } from './net/prediction';
import { EntityInterpolator } from './net/interpolation';
import { HUD } from './renderer/hud';
import { Scoreboard } from './ui/screens/scoreboard';
import { DeathScreen } from './ui/screens/death-screen';
import { MainMenu, GAME_MODES } from './ui/screens/main-menu';
import { setMatchmakingLabel } from './ui/screens/matchmaking';
import { DebugDisplay } from './utils/debug';
import { audioManager } from './audio/audio-manager';
import { playWeaponSound, playFootstep, updateAudioListener, playBombTick, playReloadStart, playReloadEnd } from './audio/sounds';
import { musicSystem } from './audio/music';
import { announcer } from './audio/announcer';
import { ambientAudio } from './audio/ambient';
import { AudioOcclusionSystem } from './audio/audio-occlusion';
import { detectSurface } from './audio/surface-detect';

// New UI systems
import { Minimap } from './ui/minimap';
import { DamageNumberSystem } from './ui/damage-numbers';
import { ScopeOverlay } from './ui/scope-overlay';
import { DirectionalIndicator } from './ui/directional-indicator';
import { DamageIndicatorSystem } from './ui/damage-indicator';
import { BuyMenu } from './ui/buy-menu';
import { NetGraph } from './ui/net-graph';
import { RoundScore, type RoundPhase } from './ui/round-score';
import { applyMobileHUD } from './ui/mobile-hud';
import { notifyMobileGameplayTick } from './ui/mobile-controls-hint';
import { disposePracticeHud } from './ui/practice-hud';
import { showProfile, setAchievements, type ProfileData } from './ui/components/profile';
import { showAuthScreen, hideAuthScreen, onAuthSuccess } from './ui/screens/auth-screen';
import { getToken as getAuthToken, saveToken as saveAuthToken, isTokenExpired } from './auth/token-store';

// Graphics systems
import { PostProcessing } from './renderer/postprocessing';
import { createAtmosphere, type AtmosphereSystem } from './renderer/atmosphere';
import {
  spawnEnhancedMuzzleFlash, spawnBulletHole,
  warmEffectTextures, EFFECT_LAYER, detectSurfaceType,
} from './renderer/enhanced-effects';
import type { SurfaceType } from './renderer/enhanced-effects';
import { ShellCasingSystem } from './renderer/shell-casings';
import { RecoilKickSystem } from './renderer/recoil-kick';
import { InspectAnimationSystem } from './renderer/inspect-animation';
import { disposeEffectTextureCache } from './renderer/effect-loop';
import { disposeEnhancedMuzzle } from './renderer/effect-muzzle';

// Map systems
import { updatePVS, getCollisionBVH } from './map/occlusion';
import { updatePropLODs, disposeProps } from './map/props';
import { generateNavMeshFromScene, disposeNavMesh } from './map/nav-mesh';
import { disposeDecals } from './map/decals';
import { setActiveMapId, getDefaultWeatherForMap } from './map/map-registry';
import { PuddleSystem } from './map/puddles';

import { getSceneTextureStreamer, updateVegetation } from './renderer/scene';
import { SkinLoader } from './cosmetics/skin-loader';
import { SKIN_MANIFEST } from './cosmetics/skin-manifest';
import { LoadingScreen } from './cosmetics/loading-screen';
import { SkinSelector } from './ui/skin-selector';
import { WeaponSkinLoader } from './cosmetics/weapon-skin-loader';
import type { WeaponLoadout } from './cosmetics/weapon-skin-types';
import { SkinMarketUI } from './ui/skin-market';
import { SkinPreviewRenderer } from './ui/skin-preview';

// Settings & perspective system
import { settingsStore, type GameSettings, type GraphicsQuality } from './core/settings';
import { SettingsMenu } from './ui/settings-menu';

// Spectator system
import { SpectatorSystem } from './spectator/spectator';
import { SpectatorHUD } from './spectator/spectator-hud';
import { XRayOverlay } from './spectator/spectator-xray';
import { AutoDirector } from './spectator/auto-director';

// Ragdoll & weapon drop rendering
import { RagdollRenderer } from './renderer/ragdoll-renderer';
import { WeaponDropManager } from './gameplay/weapon-pickup';

// Kill/headshot UI effects
import { KillEffect } from './ui/kill-effect';
import { HeadshotIndicator } from './ui/headshot-indicator';

// Communication wheel
import { PingWheel } from './ui/ping-wheel';

// Voice indicators
import { VoiceIndicators } from './ui/voice-indicators';
import { voiceChat } from './audio/voice';
import { voiceInput } from './input/voice-input';

// Mobile systems
import { AimAssist, type AimAssistSettings } from './input/aim-assist';
import { HapticFeedback } from './input/haptics';
import { ThermalThrottleDetector } from './input/thermal-throttle';
import { RotateOverlay } from './ui/rotate-overlay';
import { MobileTutorial } from './ui/mobile-tutorial';
import { showGyroPermissionBanner } from './ui/gyro-permission-banner';
import { AudioUnlock } from './audio/audio-unlock';
import { HudLayoutEditor } from './ui/hud-layout-editor';
import { QuickBuyController } from './ui/quick-buy';
import { isMobile } from './core/device';

// Team selection
import { TeamSelectMenu, type TeamChoice } from './ui/team-select';

// Registration & Leaderboard
import { RegistrationDialog } from './ui/registration-dialog';
import { LeaderboardPanel } from './ui/leaderboard-panel';
import { SocialPanel } from './ui/social-panel';
import { MapVotePanel } from './ui/map-vote-panel';

// Clan & Tournament panels
import { ClanPanel } from './ui/clan-panel';
import { TournamentPanel } from './ui/tournament-panel';
import { CaseOpenerPanel } from './ui/case-opener-panel';

// Vote-kick, Overwatch, and Progression panels
import { VotePanel } from './ui/vote-panel';
import { OverwatchPanel } from './ui/overwatch-panel';
import { ProgressionPanel } from './ui/progression-panel';

// Menu 3D background (cinematic camera orbit)
import { MenuBackground } from './ui/menu-background';

// Grenade system
import { type GrenadeId } from './core/config';
import { createEmptyInventory, type GrenadeInventory } from './gameplay/grenade';
import { GrenadeProjectileManager } from './gameplay/grenade-projectile';
import { GrenadeTrajectory } from './gameplay/grenade-trajectory';
import { type SmokeCloudHandle, type FireZoneHandle, disposeEffectPools } from './renderer/grenade-effects';
import { initSmokeFog, updateSmokeFog, disposeSmokeFog } from './renderer/smoke-fog';
import { disposeInteractionPrompt } from './ui/interaction-prompt';

// Bomb 3D entity
import { BombEntity, createBombSiteMarkers } from './gameplay/bomb';

// Chat & radio
import { ChatUI } from './ui/chat';
import { ChatComposer } from './ui/chat-composer';

// Server browser (multi-session)
import { ServerBrowser } from './ui/screens/server-browser';

// Aimpunch (camera flinch on damage)
import { updateAimpunch } from './gameplay/aimpunch';
import { setTPPWeaponPitch } from './gameplay/tpp-weapon';

// End screen callbacks
import { onPlayAgain, onReturnToMenu, hideEndScreen } from './ui/screens/end-screen';

// Replay systems
import { DeathReplaySystem, type DeathReplaySnapshot, type DeathReplayPlayerState } from './ui/death-replay';
import { ReplayRecorder, type SnapshotInput, type ReplayData } from './replay/replay-recorder';
import { ReplayStorage } from './replay/replay-storage';
import { ReplayMenu } from './ui/replay-menu';
import { ReplayPlayer } from './replay/replay-player';
import { ReplayRenderer } from './replay/replay-renderer';
import { ReplayUI } from './replay/replay-ui';

// Test instrumentation (dev only)
import { installTestHooks } from './utils/test-hooks';

// Extracted modules
import { type GameState, type GameContext } from './game-types';
import {
  handleKeyDown, handleKeyUp, toggleScope, forceUnscope,
  applySettings, buyWeapon, buyEquipment, updateMoneyDisplay,
  throwGrenade, buyGrenade, switchWeapon,
} from './game-input-handlers';
import {
  handleServerMessage, type NetworkCallbacks, cleanupNetworkTimers,
} from './game-network-handlers';
import {
  startGame, enterOfflineMode, spawnOfflineBot, createLocalPlayerBody,
} from './game-init';

// ── Reusable temp vectors for fire raycast — avoids per-shot allocations (PERF-03) ──
const _tmpOrigin = new THREE.Vector3();
const _tmpDir = new THREE.Vector3();
const _tmpAimPoint = new THREE.Vector3();
const _tmpRight = new THREE.Vector3();
const _tmpFwd = new THREE.Vector3();
const _tmpMuzzle = new THREE.Vector3();
const _UP = new THREE.Vector3(0, 1, 0);
const _tmpAimRaycaster = new THREE.Raycaster();

// ── Reusable temp vectors for per-frame hot paths (grenade preview, killcam) ──
const _tmpGrenadeEye = new THREE.Vector3();
const _tmpCamDesired = new THREE.Vector3();
const _tmpCamTarget = new THREE.Vector3();
const _tmpCamOffset = new THREE.Vector3();
const _tmpCamLookAt = new THREE.Vector3();

/**
 * Convert an ExtendedSnapshot (from binary delta decoding) into the data object
 * format that handleSnapshot() expects (which mirrors the JSON snapshot structure).
 */
function buildSnapshotDataFromExtended(snap: ExtendedSnapshot, localPlayerId: number): Record<string, any> {
  // Build players map keyed by string ID, matching the JSON { players: { "1": {...}, "2": {...} } } format
  const playersMap: Record<string, any> = {};
  for (const p of snap.players) {
    playersMap[String(p.id)] = {
      id: p.id,
      position: p.position,
      yaw: p.yaw,
      pitch: p.pitch,
      health: p.health,
      armor: p.armor,
      // CN-2: expose both weaponId (for local code) and weapon (for handleSnapshot remote-player branch)
      weaponId: p.weapon,
      weapon: p.weapon,
      isCrouching: p.isCrouching,
      isGrounded: p.isGrounded,
      isSliding: p.isSliding,
      skinId: p.skinId,
      // CN-3: forward extended fields present on binary delta payload
      money: p.money,
      team: p.team,
      grenades: p.grenades,
      // F-NC-001 — Forward every replicated PlayerSnapshot field decoded by
      // the binary path so handleSnapshot's consumers (reload/inspect/bomb-
      // carrier/ladder/mantle/water/owned-weapons/weapon-skin) see the same
      // payload they would on the legacy JSON path. Each was previously
      // dropped silently, which made every recently-bumped Protocol v6/v7/v8
      // feature no-op in production despite passing tests.
      weaponSkinId: p.weaponSkinId,
      reloading: p.reloading,
      inspecting: p.inspecting,
      isBombCarrier: p.isBombCarrier,
      isOnLadder: p.isOnLadder,
      ladderId: p.ladderId,
      isMantling: p.isMantling,
      mantleDest: p.mantleDest,
      mantleT: p.mantleT,
      isInWater: p.isInWater,
      ownedWeapons: p.ownedWeapons,
      // lastInputSeq comes from the per-player yourSequenceAck for local player
      lastInputSeq: p.id === localPlayerId ? snap.yourSequenceAck : undefined,
      // These fields are populated from FlatBuffer full snapshots but may not
      // be in delta — use defaults so handleSnapshot doesn't error
      isAlive: p.health > 0,
    };
  }

  const data: Record<string, any> = {
    tick: snap.tick,
    players: playersMap,
    events: snap.events,
  };

  if (snap.timeRemaining !== undefined) data.timeRemaining = snap.timeRemaining;
  if (snap.roundPhase !== undefined) data.roundPhase = snap.roundPhase;
  if (snap.roundNumber !== undefined) data.roundNumber = snap.roundNumber;
  if (snap.teamScores !== undefined) data.teamScores = snap.teamScores;
  if (snap.bomb !== undefined) data.bomb = snap.bomb;
  if (snap.doorStates !== undefined) data.doorStates = snap.doorStates;

  return data;
}

export class Game implements GameContext {
  // Core systems
  loop!: GameLoop;
  clock = new GameClock();
  scene!: THREE.Scene;
  renderer!: THREE.WebGLRenderer;
  camera!: FPSCamera;

  // Gameplay
  localPlayer = new LocalPlayer();
  remotePlayers = new Map<number, RemotePlayer>();
  weaponState!: WeaponState;
  viewmodel!: WeaponViewmodel;

  // Networking
  connection!: Connection;
  prediction!: PredictionSystem;
  interpolator = new EntityInterpolator();
  private deltaDecoder = new DeltaDecoder();
  private lastExtendedState: ExtendedSnapshot | null = null;

  // UI
  hud!: HUD;
  scoreboard!: Scoreboard;
  deathScreen!: DeathScreen;
  mainMenu!: MainMenu;
  debug = new DebugDisplay();

  // New UI systems
  minimap!: Minimap;
  damageNumbers!: DamageNumberSystem;
  scopeOverlay!: ScopeOverlay;
  directionalIndicator!: DirectionalIndicator;
  damageIndicator!: DamageIndicatorSystem;
  buyMenu!: BuyMenu;
  netGraph!: NetGraph;
  roundScore!: RoundScore;

  // Audio occlusion
  private audioOcclusion = new AudioOcclusionSystem();

  // Graphics systems
  postProcessing!: PostProcessing;
  atmosphere!: AtmosphereSystem;
  puddleSystem!: PuddleSystem;

  volumetricSmoke: any = null;

  // Pooled shell casings — single InstancedMesh (50 slots) with physics bounce
  shellCasings!: ShellCasingSystem;

  // Spring-based visual recoil (layered on top of per-shot instant-snap kick
  // for a more organic camera+viewmodel feel). Visual only — does NOT affect
  // server-authoritative aim.
  recoilKick = new RecoilKickSystem();

  // Procedural weapon inspect animations (flip/checkChamber/spin/admire)
  inspectAnimation = new InspectAnimationSystem();
  private inspectAnimElapsed = 0;

  // Skin system
  skinLoader = new SkinLoader();
  loadingScreen = new LoadingScreen();
  skinSelector!: SkinSelector;
  localPlayerBody: THREE.Group | null = null;
  localTppBombAttached = false;
  private _lastBodyVisible: boolean | undefined = undefined;

  // Weapon skin system
  weaponSkinLoader!: WeaponSkinLoader;
  weaponLoadout: WeaponLoadout = { equipped: {} };

  // Slot keybind table — see gameplay/loadout.ts. Defaults to spectator
  // (knife only) until the server reports a team.
  loadoutSlots: LoadoutSlots = defaultLoadoutForTeam(0);

  // Marketplace & skin preview (optional UI)
  skinMarket?: SkinMarketUI;
  skinPreview?: SkinPreviewRenderer;

  // Settings & perspective
  settingsMenu!: SettingsMenu;
  private onSettingsChange: (s: GameSettings) => void;

  // Spectator
  spectator = new SpectatorSystem();
  spectatorHud!: SpectatorHUD;
  xrayOverlay!: XRayOverlay;
  autoDirector!: AutoDirector;

  // Ragdoll & weapon drop rendering
  ragdollRenderer!: RagdollRenderer;
  weaponDropMgr!: WeaponDropManager;

  // Kill/headshot UI effects
  killEffect!: KillEffect;
  headshotIndicator!: HeadshotIndicator;

  // Communication wheel (mobile only)
  pingWheel: PingWheel | null = null;

  // Voice indicators
  voiceIndicators!: VoiceIndicators;

  // Mobile-only systems (null on desktop)
  touchInput: TouchInputProvider | null = null;
  aimAssist: AimAssist | null = null;
  hapticFeedback: HapticFeedback | null = null;
  /** G-H-002: Mobile thermal-throttle detector — null on desktop.
   *  Watches frame-time variance and downgrades graphicsQuality one
   *  tier when sustained throttling is detected. */
  thermalThrottle: ThermalThrottleDetector | null = null;
  /** BUG-MT-10: last devicePixelRatio applied from thermalThrottle.getCurrentDpr().
   *  Tracked so we only push setPixelRatio/setSize when the value actually changes.
   *  -1 forces a refresh on the first frame. */
  private _lastDpr = -1;
  /** BUG-MT-5: latched mobile ADS state. When true on a non-scoped weapon we
   *  apply a temporary FOV reduction. Tracks the previous frame's value so we
   *  only mutate camera.fov on rising/falling edges. */
  private _mobileAdsActive = false;
  /** Saved base FOV restored when exiting mobile ADS. */
  private _mobileAdsBaseFov = 0;
  rotateOverlay: RotateOverlay | null = null;
  mobileTutorial: MobileTutorial | null = null;
  hudLayoutEditor: HudLayoutEditor | null = null;
  /** MOB-W6: customizable freeze-time quick-buy buttons. Null on desktop. */
  quickBuy: QuickBuyController | null = null;
  // MOB-H5: Audio context unlock for mobile Safari (no-op on desktop, harmless to run).
  audioUnlock: AudioUnlock | null = null;

  private _cachedSpectatorList: Array<{ id: number; name: string; alive: boolean; team: number; isTarget: boolean }> | null = null;
  private _lastSpectatorPlayerCount = -1;
  private _lastSpectatorTargetId = -1;

  // Team selection
  teamSelectMenu!: TeamSelectMenu;

  // Registration & leaderboard
  registrationDialog!: RegistrationDialog;
  leaderboardPanel!: LeaderboardPanel;
  // W-C-003: friends panel; gives the /api/friends* social endpoints their
  // first client UI caller.
  socialPanel!: SocialPanel;
  // W-C-004: casual map-vote panel; the only client caller for GET/POST /api/map-vote.
  mapVotePanel!: MapVotePanel;

  // Clan & Tournament panels
  clanPanel!: ClanPanel;
  tournamentPanel!: TournamentPanel;
  casePanel!: CaseOpenerPanel;

  // Vote-kick, Overwatch, Progression panels
  votePanel!: VotePanel;
  overwatchPanel!: OverwatchPanel;
  progressionPanel!: ProgressionPanel;

  // Server browser (multi-session)
  serverBrowser!: ServerBrowser;

  // Menu 3D background (cinematic camera orbit)
  menuBackground = new MenuBackground();

  // Death replay system (slow-motion killer POV replay on death)
  deathReplay = new DeathReplaySystem();

  // Match replay recording
  replayRecorder = new ReplayRecorder();

  // Replay menu (accessed from main menu)
  replayMenu!: ReplayMenu;

  // Replay playback (created on-demand when watching a saved replay)
  private replayPlayer: ReplayPlayer | null = null;
  private replayRenderer: ReplayRenderer | null = null;
  private replayUI: ReplayUI | null = null;

  // State
  state: GameState = 'menu';
  serverUrl = '';
  footstepTimer = 0;
  money = 100000;
  isScoped = false;
  shakeOffset = { x: 0, y: 0 };

  // Killcam state
  killcamActive = false;
  killcamTarget: RemotePlayer | null = null;
  killcamTimer = 0;
  readonly KILLCAM_DURATION = 3; // seconds

  // Round state (client-side tracking)
  tScore = 0;
  ctScore = 0;
  roundNum = 1;
  roundPhase: RoundPhase = 'active';

  // F-NC-003 — Reconnect token captured from `welcome.data.reconnectToken`.
  // Restored from sessionStorage on Game construction so a tab refresh that
  // reconnects within the abandon-disconnect grace period (3 min) is treated
  // as a reconnect rather than a fresh join. Cleared by clearAuthToken-like
  // logic in main-menu/leave paths.
  reconnectToken: string | null = (() => {
    try {
      return typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('cp.reconnectToken') : null;
    } catch {
      return null;
    }
  })();

  // Wallbang indicator
  wallbangTimer = 0;
  wallbangEl: HTMLElement | null = null;

  // Death-to-spectator transition
  deathToSpectatorTimer = 0;

  // Bomb 3D entity
  bombEntity = new BombEntity();
  private disposeBombSiteMarkers: (() => void) | null = null;
  private bombTickTimer = 0; // countdown for next bomb tick sound

  // Chat & radio
  chatUI!: ChatUI;
  /** F-MOB-H3 (chat): mobile-only touch-friendly chat composer. Lazily
   *  instantiated alongside chatUI when the device is mobile; null on
   *  desktop. */
  chatComposer: ChatComposer | null = null;

  // Grenade system
  grenadeInventory: GrenadeInventory = createEmptyInventory();
  grenadeProjectiles = new GrenadeProjectileManager();
  grenadeTrajectory = new GrenadeTrajectory();
  activeSmokeZones: SmokeCloudHandle[] = [];
  activeFireZones: FireZoneHandle[] = [];
  flashBlindTimer = 0;
  selectedGrenade: GrenadeId | null = null;
  previousWeaponBeforeGrenade: WeaponId | null = null;
  grenadeThrowAnimTimer = 0;
  // BUG-MT-20: when a grenade drains to 0 after a throw, the GRN cycle would
  // restart from index 0 (HE) next press. Remember the slot the player was on
  // so the next cycle resumes from there instead of jumping back to the start.
  lastNonEmptyGrenadeIndex: number = -1;

  // Recent game events buffer for auto-director scoring
  recentEvents: Array<{ type: string; data: any }> = [];

  // Render timing (actual frame dt instead of hardcoded 1/60)
  private lastRenderTime = 0;

  // Resize handler for post-processing
  private _onResize: (() => void) | null = null;
  private _onSettingsFovChange: ((e: Event) => void) | null = null;

  // Bound event handlers (stored so we can remove them in dispose)
  private _onKeyDown: ((e: KeyboardEvent) => void) | null = null;
  private _onKeyUp: ((e: KeyboardEvent) => void) | null = null;
  private _onMouseDown: ((e: MouseEvent) => void) | null = null;
  private _onContextMenu: ((e: Event) => void) | null = null;

  // BUG-PARITY-H4: AbortController for the document-level `surrender-vote-initiate`
  // CustomEvent listener. The scoreboard's surrender button dispatches this event;
  // we route it back to initiateSurrenderVote() so the panel doesn't need a direct
  // reference to the Game instance. Aborted on dispose so a hot reload doesn't leak
  // the listener.
  private _surrenderVoteAbort: AbortController | null = null;

  // BUG-HUD-15: one-shot guard so notifyMobileGameplayTick() only fires for the
  // first frame after entering the 'playing' state. mobile-controls-hint also
  // self-gates internally, but this avoids a per-frame import/call on desktop.
  private _mobileGameplayTickFired = false;

  constructor() {
    // Settings change handler — bound so we can unsubscribe later.
    // Also re-syncs ping-wheel compact layout when controlMode changes
    // (simple = 4-segment compact; advanced/custom = 8-segment full).
    this.onSettingsChange = (s: GameSettings) => {
      applySettings(this, s);
      this.pingWheel?.setCompactMode(s.controlMode === 'simple');
    };
  }

  async init(): Promise<void> {
    // Renderer
    const { scene, renderer } = await createScene();
    this.scene = scene;
    this.renderer = renderer;

    // Weapon skin system — init after renderer is available
    this.weaponSkinLoader = new WeaponSkinLoader(this.renderer);
    this.weaponSkinLoader.initCache();

    // Camera — must be added to scene so its children render
    this.camera = new FPSCamera();
    this.camera.camera.position.set(0, 1.6, 5);
    this.camera.camera.layers.enable(EFFECT_LAYER); // render effect objects on layer 1
    this.scene.add(this.camera.camera);

    // Upgrade from single shadow map to Cascaded Shadow Maps (4 cascades)
    initCSM(this.camera.camera);

    // Add bomb 3D entity to scene (starts hidden) + bomb site ground markers
    this.scene.add(this.bombEntity.group);
    const markerMapId = this.scene.userData.activeMapId as string | undefined;
    this.disposeBombSiteMarkers = createBombSiteMarkers(this.scene, markerMapId);

    // Set up audio occlusion with scene collision geometry
    const bvhData = getCollisionBVH();
    if (bvhData) {
      this.audioOcclusion.setCollidableMeshes([bvhData.mesh]);
    }
    // Pass occlusion system to audio manager for playAt integration
    audioManager.setOcclusionSystem(this.audioOcclusion);

    // Generate nav mesh in background (non-blocking)
    void generateNavMeshFromScene(this.scene);

    // Pre-generate effect textures so first shot doesn't stutter
    warmEffectTextures();

    // Pools
    initPools(this.scene);

    // Shell casing pool — one draw call for up to 50 concurrent casings
    this.shellCasings = new ShellCasingSystem(this.scene);

    // Viewmodel (now uses separate scene for two-pass rendering)
    this.viewmodel = new WeaponViewmodel(this.camera.camera, 'glock');

    // Preload weapon GLB models (blocking weapons awaited, eager fire-and-forget)
    this.viewmodel.preloadWeapons().catch(() => {
      console.warn('[Game] Weapon GLB preload failed — using procedural fallback');
    });

    // Weapon
    this.weaponState = createWeaponState('glock');

    // Input
    // Desktop keyboard+mouse InputManager. On mobile we still need its
    // keydown/keyup listeners so bound keys (scoreboard, inspect, etc.) work
    // when a physical keyboard is attached, but the touch provider is the
    // primary source for the per-tick InputState + bitfield encoding.
    input.init();

    // Audio
    audioManager.init();
    audioManager.registerGameSounds();

    // Spectator HUD
    this.spectatorHud = new SpectatorHUD();

    // Spectator enhancements (X-ray overlay + auto-director)
    this.xrayOverlay = new XRayOverlay();
    this.autoDirector = new AutoDirector();

    // Ragdoll & weapon drop rendering
    this.ragdollRenderer = new RagdollRenderer(this.scene);
    this.weaponDropMgr = new WeaponDropManager(this.scene);

    // Kill/headshot UI effects
    this.killEffect = new KillEffect();
    this.headshotIndicator = new HeadshotIndicator();

    // Voice indicators (works on all devices)
    this.voiceIndicators = new VoiceIndicators(voiceChat);
    this.voiceIndicators.mount();

    // BUG-PARITY-2: communication ping wheel (cross-platform). Desktop opens
    // it via KeyQ in _onKeyDown; mobile touch listeners + the PING button are
    // wired inside the isMobile() block below.
    this.pingWheel = new PingWheel((type, position) => {
      this.connection.send({ type: 'ping', data: { pingType: type, position } });
    });
    // BUG-PARITY follow-up: simple control mode uses the 4-segment compact ping wheel.
    // Apply once based on current setting; settingsStore.onChange (registered below)
    // re-syncs whenever the user toggles control mode in settings.
    this.pingWheel.setCompactMode(settingsStore.get().controlMode === 'simple');

    // Mobile-only systems
    if (isMobile()) {
      // Inject mobile-responsive HUD CSS (safe areas, clamp font sizes, etc.)
      applyMobileHUD();

      // Touch input provider — joystick, action buttons, touch-look, optional gyro
      const gyroEnabled = settingsStore.get().gyroEnabled;
      this.touchInput = new TouchInputProvider(gyroEnabled);
      this.touchInput.init();
      // MOB-VIS: Touch overlays must not render on top of the main menu /
      // team-select. init() already hides them, but call setVisible(false)
      // explicitly so the initial state is unambiguous and survives any
      // future refactor of init().
      this.touchInput.setVisible(false);
      // Cycle primary → secondary → melee on WPN button tap. Reads the
      // per-loadout slot table so a player who bought an AK-47 cycles into
      // their rifle instead of the hardcoded default trio (the old cycle
      // never included bought primaries, stranding players in glock/usp/knife).
      this.touchInput.onWeaponSwitch(() => {
        if (this.state !== 'playing' || !this.localPlayer.alive) return;
        const next = nextWeaponInCycle(this.loadoutSlots, this.weaponState.id as WeaponId);
        if (next && next !== this.weaponState.id) switchWeapon(this, next);
      });

      // MOB-C2: SCOPE button cycles scope stages (mirrors desktop right-click).
      this.touchInput.onScope(() => {
        if (this.state !== 'playing' || !this.localPlayer.alive) return;
        toggleScope(this);
      });

      // MOB-C3: USE button mirrors desktop E — bomb plant (T) / defuse (CT) +
      // door / weapon-pickup interact. Server validates proximity for all paths.
      // BUG-MT-1: the door / weapon-pickup interact bit now rides the binary
      // ACT_USE flag via popInteract() (see encodeActionsFromState below);
      // bomb plant/defuse stay on the dedicated JSON messages.
      this.touchInput.onInteract(() => {
        if (this.state !== 'playing' || !this.localPlayer.alive) return;
        if (this.localPlayer.team === 1) {
          this.connection.send({ type: 'bomb_plant', data: {} });
        } else if (this.localPlayer.team === 2) {
          this.connection.send({ type: 'bomb_defuse', data: {} });
        }
      });

      // MOB-W4: Mobile grenade throw — fire-button release fires the callback;
      // strength is set by vertical drag during the press. Gate on selectedGrenade
      // so non-grenade fire releases are no-ops (regular fire still flows through
      // the inputState.fire path which is gated by !this.selectedGrenade).
      this.touchInput.onGrenadeFire((strength) => {
        if (this.state !== 'playing' || !this.localPlayer.alive) return;
        if (!this.selectedGrenade) return;
        throwGrenade(this, strength);
        // BUG-MT-8a: vibrate on actual release so the player feels the throw.
        this.hapticFeedback?.grenadeThrow();
      });

      // F-MOB-H3: BUY button — opens the buy menu (mirrors desktop B).
      // BUG-W-11: also gate on roundPhase === 'freeze_time' so the menu can
      // only open while buys are actually allowed (the quick-buy path on
      // line ~795 already enforces this; the full BUY button was missing it).
      this.touchInput.onBuy(() => {
        if (this.state !== 'playing' && this.state !== 'dead') return;
        if (this.roundPhase !== 'freeze_time') return;
        if (!this.buyMenu.isOpen()) {
          this.buyMenu.setGrenadeInventory(this.grenadeInventory);
          this.buyMenu.show(this.money);
        } else {
          this.buyMenu.hide();
        }
      });

      // F-MOB-H3: TAB button — toggles the scoreboard.
      // BUG-P-1: route through Scoreboard.toggle() so isVisible() reflects
      // reality instead of being out of sync with DOM-style mutations.
      this.touchInput.onScoreboard(() => {
        this.scoreboard.toggle();
      });

      // F-MOB-H3: GRN button — cycles through equipped grenades.
      // BUG-MT-20: after a throw drains a grenade to 0, selectedGrenade is
      // cleared but we remember the slot via lastNonEmptyGrenadeIndex so the
      // next press resumes from there instead of restarting at index 0 (HE).
      this.touchInput.onGrenadeCycle(() => {
        if (this.state !== 'playing' || !this.localPlayer.alive) return;
        const types: GrenadeId[] = [
          'he_grenade',
          'flashbang',
          'smoke_grenade',
          'molotov',
          'incendiary',
          'decoy_grenade',
        ];
        // Find next grenade type with > 0 inventory.
        const inv = this.grenadeInventory as unknown as Record<string, number>;
        let startIdx: number;
        if (this.selectedGrenade) {
          startIdx = types.indexOf(this.selectedGrenade);
        } else if (this.lastNonEmptyGrenadeIndex >= 0) {
          // Resume from the slot we were on before the inventory drained.
          // Offset by -1 so the (startIdx + 1) step below lands back on it.
          startIdx = this.lastNonEmptyGrenadeIndex - 1;
        } else {
          startIdx = -1;
        }
        for (let i = 1; i <= types.length; i++) {
          const idx = (startIdx + i) % types.length;
          const t = types[idx];
          if ((inv[t] ?? 0) > 0) {
            this.selectedGrenade = t;
            this.lastNonEmptyGrenadeIndex = idx;
            return;
          }
        }
        // No grenades available — clear selection.
        this.selectedGrenade = null;
      });

      // F-MOB-H3: PING button — opens the ping wheel at screen center.
      this.touchInput.onPing(() => {
        if (this.state !== 'playing' || !this.pingWheel) return;
        if (!this.pingWheel.isVisible()) {
          this.pingWheel.show(window.innerWidth / 2, window.innerHeight / 2);
        }
      });

      // HUD layout editor — allows drag/resize of touch buttons.
      // Entered via the `editHudLayout` keybind (default F9). Preset layouts
      // + per-button positions persist to localStorage.
      const touchButtons = this.touchInput.getButtons();
      if (touchButtons) {
        this.hudLayoutEditor = new HudLayoutEditor(touchButtons);
        // MT-7: wire the mobile VOTE button (advanced control mode) to the
        // map-vote panel. Mirrors the desktop keybind path at line ~1308.
        // MT-7 polish: if a vote isn't active, surface a HUD toast rather
        // than silently opening an empty panel. We probe the server's
        // /api/map-vote endpoint synchronously-ish (one fetch per tap) and
        // either toggle the panel or post a kill-feed-style notice.
        touchButtons.onMapVote = () => this.toggleMapVoteWithGuard();
        // BUG-PARITY-H2: wire the mobile RADIO button (advanced control mode)
        // to the radio command wheel. Mirrors the desktop Z/X/C submenu path;
        // openRadioMenu() without a menuKey opens the mobile tap-to-select
        // wheel (all three rows visible at once).
        touchButtons.onRadio = () => this.chatUI.openRadioMenu();
        // BUG-MT-30: PTT visual feedback. voiceChat fires onChange whenever
        // _transmitState transitions; reflect the 'live' state onto the
        // #btn-ptt element by toggling the .ptt-transmitting class so the
        // player sees a confirmed-transmitting indicator while held.
        // (voice.ts has no dedicated onTransmitStateChange — onChange is the
        // global state-notify channel.)
        voiceChat.onChange(() => {
          const state = voiceChat.getTransmitState();
          touchButtons.setPTTTransmitting(state === 'live');
        });
      }

      // MOB-W6: customizable freeze-time quick-buy buttons. Default to T
      // until the team-snapshot lands; setTeam() in game-network-handlers
      // re-derives defaults if no saved loadout exists. The buy handler
      // routes through the same buyWeapon() entry the desktop BuyMenu uses
      // so the wire path is identical.
      this.quickBuy = new QuickBuyController({
        team: 'T',
        triggerBuy: (weaponId) => buyWeapon(this, weaponId),
      });
      // Apply the initial loadout to button labels so the player sees the
      // default weapons before the first round even if they never open the
      // settings menu.
      if (touchButtons) {
        const initial = this.quickBuy.getLoadout();
        for (let i = 0; i < initial.length; i++) {
          touchButtons.setQuickBuyLabel(i as 0 | 1 | 2, this.quickBuy.getButtonLabel(i as 0 | 1 | 2));
        }
      }
      this.touchInput.onQuickBuy((slot) => {
        if (this.state !== 'playing' && this.state !== 'dead') return;
        // Only meaningful during freeze_time when buys are allowed; gate
        // mirrors the BuyMenu's buyAllowed check.
        if (this.roundPhase !== 'freeze_time') return;
        this.quickBuy?.triggerBuy(slot);
      });

      // BUG-MT-2: forward touchmove / touchend to the ping wheel only while it
      // is visible so segment selection works on mobile. Guarded by isVisible()
      // so resting touches never reach the wheel.
      document.addEventListener('touchmove', (e) => {
        if (!this.pingWheel || !this.pingWheel.isVisible()) return;
        const t = e.touches[0];
        if (!t) return;
        this.pingWheel.handleTouchMove(t.clientX, t.clientY);
      });
      document.addEventListener('touchend', () => {
        if (!this.pingWheel || !this.pingWheel.isVisible()) return;
        this.pingWheel.handleTouchEnd();
      });

      // Aim assist with default settings
      const aimAssistSettings: AimAssistSettings = {
        aimAssistStrength: 0.5,
        adsSnapEnabled: true,
        scopeSensitivity: {},
      };
      this.aimAssist = new AimAssist(aimAssistSettings);

      // BUG-MT-26: iOS DeviceMotionEvent requires an explicit user-gesture
      // permission prompt. Previously the banner was only shown when the
      // player flipped the gyro toggle in Settings, so first-launch users
      // never saw it. Surface the banner once per device (gated by a
      // localStorage flag) when the runtime exposes requestPermission().
      try {
        const DME = (globalThis as { DeviceMotionEvent?: { requestPermission?: () => Promise<string> } }).DeviceMotionEvent;
        const needsPrompt =
          !localStorage.getItem('gyro_prompted') &&
          typeof DME?.requestPermission === 'function';
        if (needsPrompt) {
          localStorage.setItem('gyro_prompted', '1');
          showGyroPermissionBanner({
            onRetry: async () => {
              return (await this.touchInput?.requestGyroPermission()) ?? false;
            },
          });
        }
      } catch {
        // localStorage may be unavailable (private mode); skip silently.
      }

      // Haptic feedback
      this.hapticFeedback = new HapticFeedback();

      // G-H-002: Thermal throttle detector. Watches frame-time variance
      // across a 30s sliding window; when the second half of the window
      // is >30% worse than the first half, emits one downgrade callback
      // that ratchets graphicsQuality one tier lower. Fed dt*1000 (ms)
      // from the render loop. Seed with the current quality so the
      // downgrade ladder targets the right next tier.
      this.thermalThrottle = new ThermalThrottleDetector(
        (nextTier) => {
          settingsStore.update({ graphicsQuality: nextTier as GraphicsQuality });
        },
        settingsStore.get().graphicsQuality,
      );

      // Rotate overlay (portrait → landscape prompt)
      this.rotateOverlay = new RotateOverlay();
      this.rotateOverlay.init();

      // Mobile tutorial (first-time onboarding)
      this.mobileTutorial = new MobileTutorial();
      if (this.mobileTutorial.shouldShow()) {
        this.mobileTutorial.start();
      }
    }

    // Team selection
    this.teamSelectMenu = new TeamSelectMenu((team: TeamChoice) => {
      this.connection.send({ type: 'team_select', data: { team: team.toLowerCase() } });
      this.teamSelectMenu.hide();
      if (team === 'spectator') {
        this.state = 'spectating';
        // MOB-VIS: spectating is an in-match state — show touch overlays.
        this.touchInput?.setVisible(true);
        this.spectator.enter(0, this.camera.camera.position.clone());
        this.spectatorHud.show();
        this.hud.setSpectatorMode(true);
        this.scoreboard.setSpectating(true);
        // H4: Auto-enable auto-director on spectator entry
        if (!this.autoDirector.isActive) this.autoDirector.toggle();
      } else {
        this.state = 'playing';
        this.scoreboard.setSpectating(false);
        // MOB-VIS: playing is an in-match state — show touch overlays.
        this.touchInput?.setVisible(true);
        // Start match replay recording
        if (!this.replayRecorder.isRecording()) {
          const mapName = this.scene.userData.activeMapId as string ?? 'unknown';
          this.replayRecorder.startRecording(mapName);
        }
        this.hud.show();
        this.camera.lock();
      }
    });

    // Registration dialog
    this.registrationDialog = new RegistrationDialog();

    // Settings & perspective
    this.settingsMenu = new SettingsMenu();
    // MOB-W5 / F-MOB-M5 / F-MOB-H1 / F-MOB-M6: bridge settings UI hooks back
    // into the running TouchInputProvider + HUD layout editor.
    this.settingsMenu.setOnOpenHudEditor(() => {
      this.hudLayoutEditor?.enterEditMode();
    });
    this.settingsMenu.setOnRequestGyroPermission(async () => {
      if (!this.touchInput) return false;
      return await this.touchInput.requestGyroPermission();
    });
    this.settingsMenu.setOnCalibrateGyro(() => {
      this.touchInput?.getGyro()?.calibrate();
    });
    // MOB-W6: bridge the settings menu's quick-buy section to the running
    // QuickBuyController so the dropdowns reflect (and write to) the
    // localStorage-persisted loadout.
    this.settingsMenu.setQuickBuyLoadoutGetter(() => this.quickBuy?.getLoadout() ?? []);
    this.settingsMenu.setOnQuickBuySlotChange((slot, weaponId) => {
      if (!this.quickBuy) return;
      const loadout = this.quickBuy.getLoadout();
      loadout[slot] = weaponId as typeof loadout[number];
      this.quickBuy.saveLoadout(loadout);
      const buttons = this.touchInput?.getButtons();
      if (buttons) {
        buttons.setQuickBuyLabel(slot, this.quickBuy.getButtonLabel(slot));
      }
    });
    settingsStore.onChange(this.onSettingsChange);
    // NOTE: applySettings() is deferred until after postProcessing is created (line ~412+)
    this.camera.setCollisionScene(this.scene);

    // Skin system
    await this.skinLoader.initCache();
    // Preload all 28 skin GLBs. ~1.55 MB total so sequential await is fine at boot.
    // Individual failures are caught inside preloadAll; boot never blocks on a bad asset.
    await this.skinLoader.preloadAll(SKIN_MANIFEST);
    this.skinSelector = new SkinSelector();

    // UI
    this.hud = new HUD();
    this.scoreboard = new Scoreboard();
    // BUG-PARITY-H3: Scoreboard exposes spectator nav (PREV/MODE/NEXT) on mobile.
    // Wire callbacks once here; setSpectating(true/false) is toggled at state transitions.
    this.scoreboard.setSpectatorCallbacks({
      prev: () => this.spectator.requestPrev(this.connection),
      next: () => this.spectator.requestNext(this.connection),
      cycleMode: () => this.spectator.cycleMode(),
    });
    this.deathScreen = new DeathScreen();
    this.mainMenu = new MainMenu();

    // New UI systems
    this.minimap = new Minimap();
    this.minimap.hide();
    this.damageNumbers = new DamageNumberSystem(this.scene);
    this.scopeOverlay = new ScopeOverlay();
    this.directionalIndicator = new DirectionalIndicator();
    // Arc-style damage-source indicator — complements the 4-quadrant red gradient
    // above by pinpointing the exact angle + intensity of each hit source.
    this.damageIndicator = new DamageIndicatorSystem();
    this.buyMenu = new BuyMenu((weaponId: string) => buyWeapon(this, weaponId));
    this.buyMenu.setOnBuyGrenade((grenadeId) => buyGrenade(this, grenadeId));
    this.buyMenu.setOnBuyEquipment((equipmentId, price) => buyEquipment(this, equipmentId, price));
    this.netGraph = new NetGraph();
    this.roundScore = new RoundScore();
    this.roundScore.hide();

    // Chat & radio system
    this.chatUI = new ChatUI(
      (channel, message) => {
        this.connection.send({
          type: 'chat',
          data: { channel, message },
        });
        // Show locally immediately
        this.chatUI.addMessage({
          playerId: this.localPlayer.id,
          playerName: this.localPlayer.name,
          channel,
          message,
          timestamp: Date.now(),
        });
      },
      (command) => {
        this.connection.send({
          type: 'radio',
          data: { command },
        });
        // Show locally immediately
        this.chatUI.addMessage({
          playerId: this.localPlayer.id,
          playerName: this.localPlayer.name,
          channel: 'team',
          message: command,
          timestamp: Date.now(),
          isRadio: true,
        });
        audioManager.play('radio_beep');
      },
    );

    // F-MOB-H3 (chat): mobile-only touch-friendly chat composer. The
    // composer routes submitted messages through the same path the desktop
    // ChatUI uses (server `chat` message + local mirror), and pauses the
    // touch-look controller while the soft keyboard is up so vertical
    // keyboard drags don't tug the camera.
    if (isMobile() && this.touchInput) {
      this.chatComposer = new ChatComposer({
        onSubmit: (channel, text) => {
          this.connection.send({ type: 'chat', data: { channel, message: text } });
          this.chatUI.addMessage({
            playerId: this.localPlayer.id,
            playerName: this.localPlayer.name,
            channel,
            message: text,
            timestamp: Date.now(),
          });
        },
        setTouchLookEnabled: (enabled) => {
          this.touchInput?.setTouchLookEnabled(enabled);
        },
      });
      this.touchInput.onChat(() => {
        // Default to team chat (matches desktop Enter — Shift+Enter goes
        // all-chat there). The composer's channel pill lets the user flip
        // to all-chat with a tap.
        this.chatComposer?.open('team');
      });
    }

    // Graphics systems
    this.postProcessing = new PostProcessing(this.renderer, this.scene, this.camera.camera);
    this.atmosphere = createAtmosphere(this.scene);

    // Apply per-map default weather
    const mapId = this.scene.userData.activeMapId as string | undefined;
    if (mapId) {
      this.atmosphere.setWeather(getDefaultWeatherForMap(mapId));
    }

    // Phase 8: Water puddle system
    const puddleQuality = settingsStore.get().graphicsQuality;
    this.puddleSystem = new PuddleSystem(this.scene, puddleQuality);
    if (mapId) this.puddleSystem.loadMapPuddles(mapId);



    // Volumetric smoke system
    {
      const { VolumetricSmokeSystem } = await import('./renderer/volumetric-smoke');
      const { setVolumetricSmokeSystem } = await import('./renderer/grenade-effects');
      this.volumetricSmoke = new VolumetricSmokeSystem(this.scene, this.camera.camera) as any;
      setVolumetricSmokeSystem(this.volumetricSmoke as any);
      // F-RN-009: feed depth texture so smoke writes to scene depth and clouds don't bleed through walls.
      this.volumetricSmoke.setDepthTexture(this.postProcessing.getDepthTexture());
    }

    // R8 — pre-create the smoke fog DOM overlay so the first nade doesn't pay
    // a layout reflow cost on the next frame. updateSmokeFog still lazily
    // ensures the overlay exists, but front-loading it keeps gameplay smooth.
    initSmokeFog();

    // Apply saved settings now that postProcessing + volumetricSmoke are ready
    applySettings(this, settingsStore.get());

    // Apply saved FOV from extended settings (must be after camera creation)
    {
      const { loadExtendedSettings } = await import('./ui/settings-menu');
      const extSettings = loadExtendedSettings();
      if (extSettings.fov !== 90) {
        this.camera.camera.fov = extSettings.fov;
        this.camera.camera.updateProjectionMatrix();
      }
    }

    // Listen for FOV changes from settings menu
    this._onSettingsFovChange = ((e: Event) => {
      const customEvent = e as CustomEvent<{ fov: number }>;
      if (!this.isScoped) {
        this.camera.camera.fov = customEvent.detail.fov;
        this.camera.camera.updateProjectionMatrix();
      }
    });
    window.addEventListener('settings-fov-change', this._onSettingsFovChange);

    // Central resize handler — keeps renderer, postprocessing, camera,
    // and viewmodel framebuffers in sync atomically (prevents flicker).
    this._onResize = () => {
      const w = window.innerWidth;
      const h = window.innerHeight;
      this.renderer.setSize(w, h);
      this.postProcessing.setSize(w, h);
      this.camera.handleResize(w, h);
      this.viewmodel.handleResize(w, h);
      // F-RN-010: keep smoke depth render-target sized to the new framebuffer + re-bind postprocessing depth tex.
      if (this.volumetricSmoke) {
        this.volumetricSmoke.setResolution(w, h);
        this.volumetricSmoke.setDepthTexture(this.postProcessing.getDepthTexture());
      }
    };
    window.addEventListener('resize', this._onResize);

    // Wallbang indicator element
    this.wallbangEl = document.getElementById('wallbang-indicator');

    // Prediction
    const applyInputFn: ApplyInputFn = (state: PlayerState, inputMsg: InputMessage): PlayerState => {
      const fakePlayer = new LocalPlayer();
      fakePlayer.position.set(state.x, state.y, state.z);
      fakePlayer.yaw = state.yaw;
      fakePlayer.pitch = state.pitch;
      fakePlayer.isCrouching = state.isCrouching;
      fakePlayer.isGrounded = state.isGrounded;
      fakePlayer.isSliding = state.isSliding;
      fakePlayer.currentWeapon = this.localPlayer.currentWeapon;

      // Copy velocity and movement state from the live local player so re-simulation
      // starts with correct momentum (gravity, slide, tag, etc.)
      fakePlayer.velocity.copy(this.localPlayer.velocity);
      fakePlayer.tagSlowdown = this.localPlayer.tagSlowdown;
      fakePlayer.tagTimer = this.localPlayer.tagTimer;
      fakePlayer.slideTimer = this.localPlayer.slideTimer;
      fakePlayer.slideCooldownTimer = this.localPlayer.slideCooldownTimer;
      fakePlayer.slideDirection.x = this.localPlayer.slideDirection.x;
      fakePlayer.slideDirection.z = this.localPlayer.slideDirection.z;

      const inputState: InputState = {
        forward: !!(inputMsg.keys & 1),
        back: !!(inputMsg.keys & 4),
        left: !!(inputMsg.keys & 2),
        right: !!(inputMsg.keys & 8),
        jump: !!(inputMsg.keys & 16),
        crouch: !!(inputMsg.keys & 32),
        walk: !!(inputMsg.keys & 64),
        fire: !!(inputMsg.actions & 1),
        reload: !!(inputMsg.actions & 2),
        ads: false,
        mouseDx: 0,
        mouseDy: 0,
      };

      applyInput(fakePlayer, inputState, 1 / TICK_RATE);

      return {
        x: fakePlayer.position.x,
        y: fakePlayer.position.y,
        z: fakePlayer.position.z,
        yaw: fakePlayer.yaw,
        pitch: fakePlayer.pitch,
        isCrouching: fakePlayer.isCrouching,
        isGrounded: fakePlayer.isGrounded,
        isSliding: fakePlayer.isSliding,
      };
    };
    this.prediction = new PredictionSystem(applyInputFn);

    // Networking — use VITE_WS_URL env var for production, fallback to same-host:8080
    const envWsUrl = import.meta.env.VITE_WS_URL;
    if (envWsUrl) {
      this.serverUrl = envWsUrl;
    } else {
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      this.serverUrl = `${protocol}//${window.location.hostname}:8080/ws`;
    }
    this.connection = new Connection(this.serverUrl);
    this.connection.setClock(this.clock);

    // Derive HTTP base URL from WS URL for REST API calls
    const httpBaseUrl = this.serverUrl
      .replace(/^wss:/, 'https:')
      .replace(/^ws:/, 'http:')
      .replace(/\/ws$/, '');

    // Server browser (multi-session match support)
    this.serverBrowser = new ServerBrowser(httpBaseUrl);
    this.serverBrowser.setOnJoinMatch((matchId: string) => {
      this.serverBrowser.hide();
      this.connection.setMatchId(matchId);
      startGame(this, () => this._enterOfflineMode());
    });
    this.serverBrowser.setOnBack(() => {
      this.serverBrowser.hide();
      this.mainMenu.show();
      this.skinSelector.show();
    });

    // Marketplace — the skin preview renderer is constructed lazily on the
    // first market open so the WebGL renderer isn't allocated for players
    // who never visit the marketplace.
    this.skinMarket = new SkinMarketUI(httpBaseUrl, {
      skinLoader: this.weaponSkinLoader,
    });
    this.skinMarket.init(document.body);
    this.skinMarket.setOnBack(() => {
      this.mainMenu.show();
      this.skinSelector.show();
    });

    // Replay menu
    this.replayMenu = new ReplayMenu();
    this.replayMenu.setOnPlayReplay((replay) => {
      this.enterReplayMode(replay);
    });
    this.replayMenu.setOnBack(() => {
      this.mainMenu.show();
      this.skinSelector.show();
    });

    // Leaderboard panel (must be after serverUrl is set)
    this.leaderboardPanel = new LeaderboardPanel(this.serverUrl);
    // W-C-003: social/friends panel (derives the HTTP base from serverUrl).
    this.socialPanel = new SocialPanel(this.serverUrl);
    // W-C-004: map-vote panel (derives the HTTP base from serverUrl).
    this.mapVotePanel = new MapVotePanel(this.serverUrl);
    // TODO(map-vote-votes-feed): MapVotePanel has no `setVotesByMap` setter
    // today — it polls GET /api/map-vote internally every 2s and renders
    // `state.results` (Record<string, number>) directly inside its own
    // render(). If we want game.ts to push vote tallies from a snapshot/event
    // path (e.g., to avoid the extra HTTP poll while the panel is open), we
    // need to: (1) add MapVotePanel.setVotesByMap(votes: Record<string,
    // number>) that updates `lastState.results` and re-renders, and (2) gate
    // poll() so server-pushed updates win over the polled value. Until that
    // setter exists, the panel is the single source of truth for vote
    // counts — game.ts cannot wire it.

    // Clan & Tournament panels (must be after httpBaseUrl is set)
    this.clanPanel = new ClanPanel(httpBaseUrl);
    this.clanPanel.setOnBack(() => {
      this.mainMenu.show();
      this.skinSelector.show();
    });
    // F-CM-009: case-opener panel — uses the existing CaseOpener animation
    // controller; rolls happen client-side until POST /api/cases/open ships.
    this.casePanel = new CaseOpenerPanel();
    this.casePanel.init(document.body);
    this.casePanel.setOnBack(() => {
      this.mainMenu.show();
      this.skinSelector.show();
    });

    this.tournamentPanel = new TournamentPanel(httpBaseUrl);
    this.tournamentPanel.setOnBack(() => {
      this.mainMenu.show();
      this.skinSelector.show();
    });

    // Vote-kick panel (wired to server vote events in game-network-handlers)
    this.votePanel = new VotePanel(document.getElementById('ui-root') ?? document.body);
    this.votePanel.onVote((vote: boolean) => {
      this.connection.send({ type: 'vote_kick_response', data: { vote } });
    });
    this.votePanel.onAbstain(() => {
      this.connection.send({ type: 'vote_kick_abstain', data: {} });
    });

    // Overwatch review panel (has built-in Escape/Close button)
    this.overwatchPanel = new OverwatchPanel(httpBaseUrl, String(this.localPlayer.id));
    this.overwatchPanel.setOnBack(() => {
      this.mainMenu.show();
      this.skinSelector.show();
    });

    // Progression panel (shown at end of match)
    this.progressionPanel = new ProgressionPanel(document.getElementById('ui-root') ?? document.body);

    const networkCallbacks: NetworkCallbacks = {
      createLocalPlayerBody: () => createLocalPlayerBody(this),
      toggleScope: () => forceUnscope(this),
    };
    this.connection.onMessage = (msg) => {
      // BUG-34: Packet loss recording moved to binary handler only — JSON snapshots
      // are the fallback path; recording in both causes double-counting.
      handleServerMessage(this, msg, networkCallbacks);
    };
    this.connection.onBinaryMessage = (data: ArrayBuffer) => {
      if (isBinarySnapshot(data)) {
        try {
          const snapshot = decodeBinarySnapshot(data, this.deltaDecoder, this.lastExtendedState);
          this.lastExtendedState = snapshot;
          this.connection.recordSnapshotReceived();

          // Convert ExtendedSnapshot to the data format handleSnapshot expects
          const snapshotData = buildSnapshotDataFromExtended(snapshot, this.localPlayer.id);
          handleServerMessage(this, { type: 'snapshot', data: snapshotData }, networkCallbacks);
        } catch (err) {
          console.warn('[Game] Failed to decode binary snapshot:', err);
        }
      }
    };
    this.connection.onDisconnect = () => {
      this.resetDisconnectedMatchState();
      if (this.state === 'playing' || this.state === 'dead' || this.state === 'spectating' || this.state === 'team_select' || this.state === 'replay') {
        this.state = 'menu';
        // MOB-VIS: menu is out-of-match — hide touch overlays.
        this.touchInput?.setVisible(false);
        this.mainMenu.show();
        this.hud.setSpectatorMode(false);
        this.hud.hide();
        this.minimap.hide();
        this.roundScore.hide();
        this.spectator.exit();
        this.spectatorHud.hide();
        this.scoreboard.setSpectating(false);
        this.teamSelectMenu.hide();
        disposePracticeHud();
      }
    };
    this.connection.onReconnect = () => {
      const joinData: Record<string, string | number | null> = {
        name: this.localPlayer.name,
        skinId: this.localPlayer.skinId,
      };
      const gameMode = this.connection.getGameMode();
      const mapId = this.connection.getMapId();
      if (gameMode) joinData.mode = gameMode;
      if (mapId) joinData.map = mapId;
      // F-NC-003 — Echo the server-issued reconnect token so the server
      // restores our saved PlayerState (position, kills, money, weapon)
      // instead of falling through to AddPlayer.
      if (this.reconnectToken) {
        joinData.reconnectToken = this.reconnectToken;
      }
      this.connection.send({ type: 'join', data: joinData });
    };

    // Keyboard bindings — delegate to extracted handlers
    this._onKeyDown = (e) => {
      // Skip gameplay keybinds when the user is typing into an input field
      // (auth screen, chat, clan dialog, etc.). handleKeyDown applies the same
      // guard, but we also need it here so the pre-delegate shortcuts below
      // (replay Escape, spectator xray/auto-director, ping wheel) don't fire.
      const target = e.target as { tagName?: string; isContentEditable?: boolean } | null;
      const editable = !!target && (
        target.isContentEditable === true ||
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.tagName === 'SELECT'
      );
      if (editable) return;

      // Replay mode: Escape exits replay
      if (this.state === 'replay' && e.code === 'Escape') {
        this.exitReplayMode();
        return;
      }
      // Spectator-only key bindings (X-ray toggle, auto-director toggle)
      if (this.state === 'spectating' || this.state === 'dead') {
        if (keybindManager.isAction('xray', e.code)) {
          this.xrayOverlay.toggle();
          return;
        }
        if (keybindManager.isAction('autoDirector', e.code)) {
          this.autoDirector.toggle();
          return;
        }
      }
      // Ping wheel — cross-platform (BUG-PARITY-2). Desktop opens via KeyQ;
      // mobile also opens via the PING button. Both share the same PingWheel.
      if (this.state === 'playing' && this.pingWheel && keybindManager.isAction('pingWheel', e.code)) {
        if (!this.pingWheel.isVisible()) {
          this.pingWheel.show(window.innerWidth / 2, window.innerHeight / 2);
        }
        return;
      }
      // W-C-004: map vote toggle. BUG-PARITY-H1: previously on KeyM, but that
      // shadowed the teamSelect keybind (also KeyM). Moved to KeyN — verified
      // unbound elsewhere — so teamSelect (KeyM) fires through handleKeyDown.
      // KeyN is not in core/keybinds.ts so no conflict.
      if (e.code === 'KeyN' && this.state !== 'menu' && this.state !== 'replay') {
        this.mapVotePanel.toggle();
        return;
      }
      handleKeyDown(this, e);
    };
    this._onKeyUp = (e) => handleKeyUp(this, e);
    document.addEventListener('keydown', this._onKeyDown);
    document.addEventListener('keyup', this._onKeyUp);

    // BUG-PARITY-H4: the scoreboard surrender button dispatches a
    // `surrender-vote-initiate` CustomEvent on document so it doesn't need
    // a direct Game reference. Route it back to initiateSurrenderVote().
    // AbortController + signal lets us clean up atomically on dispose.
    this._surrenderVoteAbort = new AbortController();
    document.addEventListener(
      'surrender-vote-initiate',
      () => this.initiateSurrenderVote(),
      { signal: this._surrenderVoteAbort.signal },
    );

    // Right-click for scope / spectator controls / grenade throwing
    this._onMouseDown = (e) => {
      if (this.state === 'spectating' && this.spectator.active) {
        if (e.button === 0) { // LMB = next target
          this.spectator.requestNext(this.connection);
        } else if (e.button === 2) { // RMB = prev target
          this.spectator.requestPrev(this.connection);
        }
        return;
      }
      if (this.state === 'playing' && this.selectedGrenade) {
        // Grenade is selected — LMB = overhand, RMB = underhand
        if (e.button === 0) {
          throwGrenade(this, 0); // overhand
        } else if (e.button === 2) {
          throwGrenade(this, 2); // underhand
        }
        return;
      }
      if (e.button === 2 && this.state === 'playing') {
        toggleScope(this);
      }
    };
    this._onContextMenu = (e) => e.preventDefault();
    document.addEventListener('mousedown', this._onMouseDown);
    document.addEventListener('contextmenu', this._onContextMenu);

    // Menu callbacks — Quick Play and Browse Servers
    this.mainMenu.setOnPlay(async () => {
      // Stop cinematic camera orbit
      this.menuBackground.stop();
      this.connection.setMatchId(null); // Quick Play = default match
      // Set selected game mode and map on connection
      const selectedMode = this.mainMenu.getSelectedMode();
      const selectedMapId = this.mainMenu.getSelectedMapId();
      this.connection.setGameMode(selectedMode);
      this.connection.setMapId(selectedMapId);

      // Rebuild scene if selected map differs from the currently loaded one
      setActiveMapId(selectedMapId);
      if (this.scene.userData.activeMapId !== selectedMapId) {
        disposeScene();
        const { scene, renderer } = await createScene();
        this.scene = scene;
        this.renderer = renderer;
      }

      // Update matchmaking subtitle with selected mode and map
      const modeInfo = GAME_MODES.find(m => m.id === selectedMode);
      const modeLabel = modeInfo ? modeInfo.label : selectedMode;
      setMatchmakingLabel(`${modeLabel} · ${selectedMapId}`);
      startGame(this, () => this._enterOfflineMode());
    });
    this.mainMenu.setOnBrowse(() => {
      this.mainMenu.hide();
      this.skinSelector.hide();
      this.serverBrowser.show();
    });

    this.mainMenu.setOnMarket(() => {
      this.mainMenu.hide();
      this.skinSelector.hide();
      if (!this.skinPreview && this.skinMarket) {
        this.skinPreview = new SkinPreviewRenderer();
        this.skinMarket.setPreviewRenderer(this.skinPreview);
      }
      this.skinMarket?.show();
    });

    this.mainMenu.setOnReplays(() => {
      this.mainMenu.hide();
      this.skinSelector.hide();
      this.replayMenu.show();
    });

    this.mainMenu.setOnClans(() => {
      this.mainMenu.hide();
      this.skinSelector.hide();
      // Wire-up: stub announcement until /api/clans/announcement lands. Set
      // before show() so the first render includes it; setAnnouncement also
      // re-renders if the panel is already visible.
      this.clanPanel.setAnnouncement({
        text: 'Welcome to the clan! Check the roster and challenge a rival clan to a friendly.',
        date: new Date().toISOString(),
      });
      this.clanPanel.show();
    });

    this.mainMenu.setOnTournaments(() => {
      this.mainMenu.hide();
      this.skinSelector.hide();
      this.tournamentPanel.show();
    });

    this.mainMenu.setOnCases(() => {
      this.mainMenu.hide();
      this.skinSelector.hide();
      this.casePanel.show();
    });

    this.mainMenu.setOnOverwatch(() => {
      this.mainMenu.hide();
      this.skinSelector.hide();
      this.overwatchPanel.show();
    });

    this.mainMenu.setOnStats(() => {
      // F-UI-008 / F-UI-013: Hydrate profile from live localPlayer counters,
      // then best-effort fetch /api/profile to fill in server aggregates
      // (wins/losses, win-rate). Render immediately so the panel never opens
      // empty; refresh in place when the response lands.
      const profileData: ProfileData = {
        displayName: this.localPlayer.name || 'Player',
        rating: 1000,
        kills: this.localPlayer.kills | 0,
        deaths: this.localPlayer.deaths | 0,
        hltvRating: 1.0,
        adr: 0,
        winRate: 0,
        wins: 0,
        losses: 0,
        ratingHistory: [],
        weaponStats: [],
        recentMatches: [],
      };
      showProfile(profileData);

      // Wire-up: stub achievements so the profile panel renders something below
      // the stats grid until a real /api/achievements endpoint lands. The
      // `unlocked` flag is derived from local counters where possible so the
      // first two cards feel real even before the server wires up. Future
      // commit can swap this for a fetch + cache.
      setAchievements([
        { id: 'first_blood', name: 'First Blood',   icon: '🩸', unlocked: (this.localPlayer.kills | 0) > 0 },
        { id: 'sharpshooter', name: 'Sharpshooter', icon: '🎯', unlocked: (this.localPlayer.kills | 0) >= 10 },
        { id: 'survivor',    name: 'Survivor',      icon: '🛡️', unlocked: false },
        { id: 'team_player', name: 'Team Player',   icon: '🤝', unlocked: false },
        { id: 'clutch_king', name: 'Clutch King',   icon: '👑', unlocked: false },
        { id: 'veteran',     name: 'Veteran',       icon: '🎖️', unlocked: false },
      ]);

      // Best-effort hydration from /api/profile. Failures (404, 5xx, network)
      // are non-fatal — the panel keeps the locally-derived snapshot.
      const playerId = String(this.localPlayer.id);
      const url = `${httpBaseUrl}/api/profile?id=${encodeURIComponent(playerId)}`;
      fetch(url, { credentials: 'include' })
        .then((res) => (res.ok ? res.json() : null))
        .then((payload) => {
          if (!payload) return;
          const account = (payload.account ?? payload) as Record<string, unknown>;
          const stats = (account.stats ?? payload.stats ?? {}) as Record<string, unknown>;
          const merged: ProfileData = {
            ...profileData,
            displayName: typeof account.displayName === 'string' && account.displayName
              ? account.displayName
              : profileData.displayName,
            kills: Number(stats.kills ?? profileData.kills) | 0,
            deaths: Number(stats.deaths ?? profileData.deaths) | 0,
            wins: Number(stats.wins ?? profileData.wins) | 0,
            losses: Number(stats.losses ?? profileData.losses) | 0,
            winRate: Number(stats.winRate ?? stats.win_rate ?? profileData.winRate) || 0,
          };
          showProfile(merged);
        })
        .catch(() => {
          /* best-effort: keep the local snapshot on any failure */
        });
    });

    this.mainMenu.setOnLeaderboard(() => {
      void this.leaderboardPanel.show();
    });

    // W-C-003: open the SocialPanel (friend list) from the main menu.
    this.mainMenu.setOnSocial(() => void this.socialPanel.show());

    // End-screen button callbacks (play again / return to menu)
    onPlayAgain(() => {
      hideEndScreen();
      this.progressionPanel.hide();
      this.votePanel.hide();
      this.overwatchPanel.hide();
      this.state = 'menu';
      // MOB-VIS: returning to menu — hide touch overlays.
      this.touchInput?.setVisible(false);
      this.menuBackground.start(this.camera.camera);
      this.mainMenu.show();
    });
    onReturnToMenu(() => {
      hideEndScreen();
      this.progressionPanel.hide();
      this.votePanel.hide();
      this.overwatchPanel.hide();
      this.state = 'menu';
      // MOB-VIS: returning to menu — hide touch overlays.
      this.touchInput?.setVisible(false);
      this.menuBackground.start(this.camera.camera);
      this.mainMenu.show();
    });

    // Game loop
    this.loop = new GameLoop(
      (dt) => this.fixedUpdate(dt),
      (alpha) => this.render(alpha),
    );
    this.loop.start();

    // Expose test hooks in dev mode for Playwright/E2E
    installTestHooks(this);

    // Show menu
    this.mainMenu.show();
    this.hud.hide();

    // Start cinematic camera orbit for menu background
    this.menuBackground.start(this.camera.camera);

    // F-AV-003: duck score/menu music while the announcer is speaking so VO
    // sits clearly above the bed. Uses the hook injection point on
    // AnnouncerSystem so tests don't need to instantiate AudioContext.
    announcer.setSpeechHooks({
      onStart: () => musicSystem.duckForAnnouncer(),
      onEnd: () => musicSystem.unduck(),
    });

    // Start menu music
    musicSystem.playMenuMusic();

    // MOB-H5: iOS Safari (and many mobile browsers) keep the AudioContext
    // suspended until the first user gesture. The playMenuMusic() above is
    // therefore inaudible on a fresh tab; AudioUnlock installs a one-shot
    // listener that resumes Howler + the music context on first touch /
    // pointer / keypress, then auto-removes itself. Mobile additionally gets
    // a "TAP TO ENABLE AUDIO" overlay so the user knows what to do.
    this.audioUnlock = new AudioUnlock();
    this.audioUnlock.init();
    if (isMobile()) {
      this.audioUnlock.showOverlay();
    }

    // First-boot auth screen — shows Login / Register / Guest options on top
    // of the menu if no saved JWT is present (or the saved one has expired).
    // `onAuthSuccess` fires for any of the three paths and persists the token.
    const existingToken = getAuthToken();
    if (!existingToken || isTokenExpired(existingToken)) {
      onAuthSuccess((token, displayName) => {
        saveAuthToken(token);
        if (displayName) this.localPlayer.name = displayName;
        hideAuthScreen();
      });
      showAuthScreen();
    }

    // M8 — start ambient audio. de_outpost_v6 is an open industrial yard.
    const ambientMapId = (this.scene.userData?.activeMapId as string | undefined) ?? '';
    ambientAudio.setPreset(ambientMapId === 'de_outpost_v6' ? 'open' : 'outdoor');

    // M9 — per-map music theme: shift the synth pitch so each map's
    // round-start sting + future stingers carry a distinct register.
    musicSystem.setMapTheme(ambientMapId);
  }

  // ── Thin wrappers for extracted functions that need cross-references ──

  private _enterOfflineMode(): void {
    enterOfflineMode(
      this,
      () => createLocalPlayerBody(this),
      () => spawnOfflineBot(this),
    );
  }

  /** Enter replay playback mode from a saved replay. */
  private enterReplayMode(replay: ReplayData): void {
    this.state = 'replay';
    this.mainMenu.hide();
    this.hud.hide();

    // Create replay playback objects
    this.replayPlayer = new ReplayPlayer();
    this.replayPlayer.load(replay);

    this.replayRenderer = new ReplayRenderer(
      this.scene,
      this.camera,
      this.spectator,
      this.skinLoader,
    );

    this.replayUI = new ReplayUI(this.replayPlayer);
    this.replayUI.setRenderer(this.replayRenderer);
    this.replayUI.onExit(() => this.exitReplayMode());
    this.replayUI.show();

    // Set round markers and player list on the UI
    this.replayUI.setRoundMarkers(
      this.replayPlayer.getRoundMarkers(),
      replay.totalTicks,
    );
    this.replayUI.setPlayerList(replay.metadata.players);

    // Enter spectator for camera control and start playback
    this.spectator.enter(0, this.camera.camera.position.clone());
    this.replayPlayer.play();
  }

  // ── Fixed update (64Hz) ──
  private fixedUpdate(dt: number): void {
    if (this.state !== 'playing' && this.state !== 'dead' && this.state !== 'spectating' && this.state !== 'replay') return;

    // ── Replay playback update ──
    if (this.state === 'replay' && this.replayPlayer) {
      const frame = this.replayPlayer.update(dt);
      if (frame && this.replayRenderer) {
        this.replayRenderer.applyFrame(frame, null);
      }
      if (this.replayUI) {
        this.replayUI.update(
          this.replayPlayer.getProgress(),
          this.replayPlayer.getCurrentTime(),
          this.replayPlayer.getTotalDuration(),
        );
      }
      // If replay finished playing, stay paused at end
      if (!this.replayPlayer.isPlaying() && !this.replayPlayer.isPaused()) {
        // Replay ended naturally
      }
      return; // Skip normal game updates during replay playback
    }

    const now = performance.now();

    this.clock.advanceTick(dt);

    const interactiveManager = this.scene?.userData.interactiveManager as
      | {
          update: (
            deltaTime: number,
            playerPos?: { x: number; y: number; z: number },
            overridePrompt?: string | null,
          ) => void;
        }
      | undefined;
    // G9 — pick the highest-priority [E] prompt: weapon pickup > door/ladder.
    // Bomb plant/defuse already drives its own progress UI when input.use is
    // pressed at the site, so we leave that channel alone here.
    let overridePrompt: string | null = null;
    const pos = this.localPlayer.position;
    const tmpPos = new THREE.Vector3(pos.x, pos.y, pos.z);
    const nearest = this.weaponDropMgr.getNearestDrop(tmpPos);
    if (nearest) {
      overridePrompt = `PICK UP ${nearest.weaponId.toUpperCase()}`;
    }
    interactiveManager?.update(
      dt,
      { x: pos.x, y: pos.y, z: pos.z },
      overridePrompt,
    );

    // ── Death replay: record snapshot every tick during gameplay ──
    if (this.state === 'playing') {
      const drPlayers = new Map<number, DeathReplayPlayerState>();
      drPlayers.set(this.localPlayer.id, {
        id: this.localPlayer.id,
        x: this.localPlayer.position.x,
        y: this.localPlayer.position.y,
        z: this.localPlayer.position.z,
        yaw: this.localPlayer.yaw,
        pitch: this.localPlayer.pitch,
        weapon: this.localPlayer.currentWeapon,
        health: this.localPlayer.health,
      });
      for (const [id, rp] of this.remotePlayers) {
        drPlayers.set(id, {
          id,
          x: rp.position.x,
          y: rp.position.y,
          z: rp.position.z,
          yaw: rp.yaw,
          pitch: rp.pitch,
          weapon: rp.currentWeapon,
          health: rp.health,
        });
      }
      this.deathReplay.recordSnapshot({
        tick: this.clock.getEstimatedServerTick(),
        timestamp: performance.now(),
        players: drPlayers,
      });
    }

    if (this.state === 'playing' && this.localPlayer.alive) {
      // F-MOB-M1 / MOB-W8: feed per-scope sensitivity into the touch input
      // provider before snapshotting. Pick scope key from current weapon scope
      // state: hipfire by default, reddot when ADS-only, scope4x when scoped
      // overlay is active. Settings.scopeSensitivity holds user-configurable
      // multipliers (defaults 1.0 / 0.8 / 0.3). We use isScoped (live state)
      // for the scope4x branch and fall through to hipfire — reddot mapping
      // is applied by the AimAssist subsystem already, so the touch-look
      // multiplier only needs the hip vs scoped distinction here.
      if (this.touchInput) {
        const scopeMap = settingsStore.get().scopeSensitivity;
        const key: 'hipfire' | 'scope4x' = this.isScoped ? 'scope4x' : 'hipfire';
        const m = scopeMap?.[key];
        if (typeof m === 'number') this.touchInput.setScopeMultiplier(m);
        // BUG-MT-6: route through AimAssist.getScopeSensitivity so the per-
        // scope multiplier owned by AimAssist composes with the settings-
        // store multiplier above (1.0 on hipfire/scope4x by default). Even
        // partial wiring keeps the AimAssist class out of dead-code land.
        if (this.aimAssist) {
          const assistMult = this.aimAssist.getScopeSensitivity(key);
          if (typeof assistMult === 'number' && assistMult !== 1.0) {
            this.touchInput.setScopeMultiplier((m ?? 1.0) * assistMult);
          }
          // Drain any pending ADS-snap delta from AimAssist.update() and apply
          // it to the camera. No visible-enemy list is wired yet (would need a
          // raycast pass + screen-space projection), so this only fires after
          // an explicit startAdsSnap() — but the update() drive keeps the
          // snap-smoothing state machine ticking instead of being orphaned.
          const snap = this.aimAssist.update(dt);
          if (snap.x !== 0 || snap.y !== 0) {
            this.camera.setAngles(
              this.camera.getYaw() + snap.x,
              this.camera.getPitch() + snap.y,
            );
          }
        }
      }
      const inputState = this.touchInput ? this.touchInput.snapshot() : input.snapshot();

      // BUG-MT-5: mobile ADS button (inputState.ads) had no consumer. There
      // is no setAimPose API on the viewmodel yet; apply a temporary FOV
      // reduction (~0.75x) on non-scoped weapons as a placebo→real bridge.
      // Scoped weapons (AUG/SG553/AWP/SSG08) handle their own scope path via
      // forceUnscope/scopeZoom in scope-overlay.ts, so we skip them here to
      // avoid double-applying FOV.
      if (this.touchInput && this.localPlayer.alive) {
        const weaponCfg = WEAPONS[this.weaponState.id];
        const isNonScoped = !weaponCfg.scopeZoom;
        const wantAds = inputState.ads && isNonScoped && !this.isScoped;
        if (wantAds && !this._mobileAdsActive) {
          // Rising edge — save current FOV and apply reduction.
          this._mobileAdsBaseFov = this.camera.camera.fov;
          this.camera.camera.fov = this._mobileAdsBaseFov * 0.75;
          this.camera.camera.updateProjectionMatrix();
          this._mobileAdsActive = true;
        } else if (!wantAds && this._mobileAdsActive) {
          // Falling edge — restore base FOV. Guard against the FOV-settings
          // event firing while ADS is active (it skips updates when isScoped
          // is true but does NOT skip on _mobileAdsActive); restoring to the
          // captured baseFov is correct because the settings handler would
          // not have run.
          this.camera.camera.fov = this._mobileAdsBaseFov;
          this.camera.camera.updateProjectionMatrix();
          this._mobileAdsActive = false;
        }
      }

      // Camera
      if (this.touchInput) {
        // Touch/gyro is the look source on mobile — pointer lock is never acquired.
        // mouseDx/mouseDy from InputState are already pre-scaled by touch/gyro
        // sensitivity and accumulate since the last snapshot.
        this.camera.setAngles(
          this.camera.getYaw() - inputState.mouseDx,
          this.camera.getPitch() - inputState.mouseDy,
        );
        this.localPlayer.yaw = this.camera.getYaw();
        this.localPlayer.pitch = this.camera.getPitch();
      } else if (this.camera.isLocked) {
        this.localPlayer.yaw = this.camera.getYaw();
        this.localPlayer.pitch = this.camera.getPitch();
      }

      // Movement (client-side prediction)
      applyInput(this.localPlayer, inputState, dt);

      // Player-vs-player collision (client prediction, matches server)
      this.resolveLocalPlayerCollision();

      // Update viewmodel animation states from movement results
      this.viewmodel.setAirborne(!this.localPlayer.isGrounded);
      this.viewmodel.setCrouching(this.localPlayer.isCrouching && !this.localPlayer.isSliding);
      this.viewmodel.setSliding(this.localPlayer.isSliding);

      // Send input to server
      const seq = this.prediction.getSequence();
      // Derive bitfields from the captured InputState so touch + keyboard
      // both produce identical wire encoding (TouchInputProvider has no
      // encodeKeys/encodeActions of its own).
      const keysBits = this.touchInput ? encodeKeysFromState(inputState) : input.encodeKeys();
      // F-GP-006: drain the touch-provider's latched inspect/drop edges so
      // mobile presses reach the server in the next ClientInput frame.
      // BUG-MT-1: also drain the touch-provider's latched USE edge so the
      // mobile USE button rides the same ACT_USE bit desktop's KeyE uses.
      const touchInspect = this.touchInput ? this.touchInput.popInspect() : false;
      const touchDrop = this.touchInput ? this.touchInput.popDrop() : false;
      const touchInteract = this.touchInput?.popInteract() ?? false;
      const actionsBits = this.touchInput
        ? encodeActionsFromState(inputState, touchInspect, touchInteract, touchDrop)
        : input.encodeActions();
      // BUG-PROTO-4: tick was dropped from InputMessage — the server
      // decoder always ignored it.
      // N-C-003: forward the frame delta in seconds so the server's
      // ClientInput.DeltaTime (decoded at slot 6) reflects the real
      // client tick duration instead of zero.
      const inputMsg: InputMessage = {
        sequence: seq,
        keys: keysBits,
        yaw: this.localPlayer.yaw,
        pitch: this.localPlayer.pitch,
        actions: actionsBits,
        weaponSlot: 0,
        dt,
      };

      this.prediction.processInput(
        {
          x: this.localPlayer.position.x,
          y: this.localPlayer.position.y,
          z: this.localPlayer.position.z,
          yaw: this.localPlayer.yaw,
          pitch: this.localPlayer.pitch,
          isCrouching: this.localPlayer.isCrouching,
          isGrounded: this.localPlayer.isGrounded,
          isSliding: this.localPlayer.isSliding,
        },
        inputMsg,
        seq,
      );

      this.connection.send({
        type: 'input',
        data: {
          seq,
          forward: inputState.forward,
          backward: inputState.back,
          left: inputState.left,
          right: inputState.right,
          jump: inputState.jump,
          crouch: inputState.crouch,
          fire: inputState.fire,
          reload: inputState.reload,
          yaw: this.localPlayer.yaw,
          pitch: this.localPlayer.pitch,
          dt,
        },
      });

      // Weapon handling
      const wasReloading = this.weaponState.isReloading;
      this.weaponState = updateReload(this.weaponState, now);
      if (wasReloading && !this.weaponState.isReloading) {
        playReloadEnd(this.weaponState.id);
      }
      this.weaponState = updateSpread(this.weaponState, now, this.localPlayer.isCrouching);

      // Fire (skip if grenade is selected — grenade throw is handled by mousedown)
      if (inputState.fire && !this.selectedGrenade && canFire(this.weaponState, now)) {
        this.weaponState = fire(this.weaponState, now);
        this.localPlayer.ammo = this.weaponState.ammo;
        // MOB-C1: fire haptic (mobile only — null on desktop)
        this.hapticFeedback?.fire();

        // Capture scoped state before unscoping (for spread/recoil reduction on scoped rifles)
        const wasScopedOnFire = this.isScoped;

        // Per-weapon recoil
        const recoilKick = getRecoilKick(this.weaponState.id, this.weaponState.shotsFired);

        // Scoped rifles (AUG/SG553): 0.5x recoil when scoped
        const scopeRecoilMult = (wasScopedOnFire && WEAPONS[this.weaponState.id].scopeZoom) ? 0.5 : 1.0;

        // Apply camera recoil (viewmodel + camera kick)
        this.viewmodel.fireRecoil(recoilKick);
        this.camera.setAngles(
          this.camera.getYaw() + recoilKick.yawKick * scopeRecoilMult,
          this.camera.getPitch() + recoilKick.pitchKick * scopeRecoilMult,
        );

        // Spring-based visual kick overlay — additive camera pitch/roll and
        // viewmodel posZ/rotX that settles via damped oscillator rather than
        // linear decay. Pure visual; aim direction already handled above.
        this.recoilKick.applyKick(this.weaponState.id);
        // Any active procedural inspect animation should abort on fire.
        this.inspectAnimation.onFire();
        // BUG-W-28: the manual WeaponInspector (orbit/zoom hold mode) lives on
        // a separate state machine from inspectAnimation. inspectAnimation.onFire
        // cancels the procedural path, but if the player is in manual inspect
        // (viewmodel.inspector.isInspecting) firing would leave the weapon
        // pinned in the centered orbit pose. Force-exit it here so fire always
        // returns control to normal viewmodel positioning.
        if (this.viewmodel.inspector.isInspecting) {
          this.viewmodel.inspector.stopInspect();
        }

        // Screen shake on heavy weapons
        if (this.weaponState.id === 'awp') {
          triggerScreenShake(0.03, 0.15);
        } else if (this.weaponState.id === 'ak47' || this.weaponState.id === 'm4a4') {
          triggerScreenShake(0.008, 0.05);
        }

        // Force-exit scope on fire (AWP + scoped rifles) — not a user toggle
        if (this.isScoped) {
          forceUnscope(this);
        }

        // Audio
        playWeaponSound(this.weaponState.id);

        // Melee weapons: no hitscan, no bullet effects — server handles melee hit detection
        const weaponCfg = WEAPONS[this.weaponState.id];
        if (weaponCfg.type !== 'melee') {
          // Client-side hitscan (visual only)
          let spread = getCurrentSpread(
            this.weaponState,
            isMoving(this.localPlayer),
            this.localPlayer.isCrouching,
            now,
          );
          // Scoped rifles: 50% spread reduction when fired while scoped
          if (wasScopedOnFire && weaponCfg.scopeZoom && weaponCfg.scopeZoom > 0) {
            spread *= 0.5;
          }

          let origin: THREE.Vector3;
          let dir: THREE.Vector3;

          if (this.camera.getMode() === 'tpp') {
            // TPP: over-the-shoulder aiming
            // Step 1: Cast ray from camera through screen center to find aim point
            _tmpOrigin.copy(this.camera.camera.position);
            const camDir = this.camera.getDirection();
            _tmpAimRaycaster.set(_tmpOrigin, camDir);
            _tmpAimRaycaster.near = 0.1;
            _tmpAimRaycaster.far = 8192;
            _tmpAimRaycaster.layers.set(0);
            const aimHits = _tmpAimRaycaster.intersectObjects(this.scene.children, true);
            // Filter: skip local player body (otherwise the aim point can land
            // on the player's own head/torso), effect meshes, PVS-hidden, and
            // invisible objects. Walk parents to catch flags on wrapper groups.
            const validHit = aimHits.find((h) => {
              if (!h.object.visible) return false;
              for (let cur: THREE.Object3D | null = h.object; cur; cur = cur.parent) {
                const ud = cur.userData;
                if (!ud) continue;
                if (ud.isLocalPlayer) return false;
                if (ud.batchedAway) return false;
                if (ud.isEffect) return false;
                if (ud.isTracer) return false;
                if (ud.isDecal) return false;
              }
              return true;
            });
            if (validHit) {
              _tmpAimPoint.copy(validHit.point);
            } else {
              _tmpAimPoint.copy(_tmpOrigin).addScaledVector(camDir, 100);
            }

            // Step 2: Shot originates from player eye position toward aim point
            const eyeHeight = getSmoothedEyeHeight(this.localPlayer);
            _tmpOrigin.copy(this.localPlayer.position);
            _tmpOrigin.y += eyeHeight;
            origin = _tmpOrigin;
            dir = _tmpDir.copy(_tmpAimPoint).sub(_tmpOrigin).normalize();
          } else {
            // FPP: camera IS the eye position
            origin = _tmpOrigin.copy(this.camera.camera.position);
            dir = _tmpDir.copy(this.camera.getDirection());
          }

          const hitResult = performHitscan(origin, dir, spread, this.scene);

          // Spawn effects — enhanced muzzle flash + shell casing
          if (this.camera.getMode() === 'tpp' && this.localPlayerBody) {
            // In TPP: spawn muzzle flash at weapon barrel position (body right hand + forward)
            _tmpFwd.copy(dir);
            _tmpRight.crossVectors(_tmpFwd, _UP);
            // Degenerate cross (aiming straight up/down): fall back to the
            // player's yaw-based right axis so we never normalize a zero vector.
            if (_tmpRight.lengthSq() < 1e-6) {
              _tmpRight.set(Math.cos(this.localPlayer.yaw), 0, -Math.sin(this.localPlayer.yaw));
            } else {
              _tmpRight.normalize();
            }
            _tmpMuzzle.copy(this.localPlayer.position);
            _tmpMuzzle.y += 1.15; // hand height
            _tmpMuzzle.addScaledVector(_tmpRight, 0.3); // right hand offset
            _tmpMuzzle.addScaledVector(_tmpFwd, 0.5); // forward to barrel tip
          } else {
            _tmpMuzzle.copy(origin).addScaledVector(dir, 0.5);
          }
          // F-RN-006: enhanced muzzle flash supersedes the legacy spawnMuzzleFlash on the local-fire path.
          spawnEnhancedMuzzleFlash(_tmpMuzzle, dir, this.scene);
          this.shellCasings.spawn(_tmpMuzzle, dir, weaponCfg.type);
          if (hitResult.hit) {
            // Detect surface type from hit material for surface-specific bullet holes
            let surfaceType: SurfaceType | undefined;
            if (hitResult.object && hitResult.object instanceof THREE.Mesh) {
              const hitMat = Array.isArray(hitResult.object.material)
                ? hitResult.object.material[0]
                : hitResult.object.material;
              if (hitMat) surfaceType = detectSurfaceType(hitMat);
            }
            spawnBulletHole(hitResult.point, hitResult.normal ?? _tmpFwd.copy(dir).negate(), this.scene, surfaceType);
            // Silenced weapons: suppress tracers
            if (!weaponCfg.silenced) {
              spawnTracer(_tmpMuzzle, hitResult.point, this.scene);
            }
            if (hitResult.hitPlayer) {
              spawnBloodEffect(hitResult.point, this.scene);
              // H3: Damage numbers now come from server 'damage' event only
              // (see game-network-handlers.ts 'damage' case) for authoritative values.
              // Client-side blood/tracer effects are still instant for responsiveness.
            }
          }

          // TPP fire animation: small recoil kick on player body
          if (this.camera.getMode() === 'tpp' && this.localPlayerBody) {
            this.localPlayerBody.rotation.x -= 0.04;
          }
        }
      } else if (inputState.fire && this.weaponState.ammo <= 0 && !this.weaponState.isReloading && WEAPONS[this.weaponState.id].type !== 'melee') {
        audioManager.play('dry_fire');
      }

      // Reload
      if (inputState.reload) {
        const prevReloading = this.weaponState.isReloading;
        this.weaponState = startReload(this.weaponState, now);
        if (!prevReloading && this.weaponState.isReloading) {
          playReloadStart(this.weaponState.id);
          this.connection.send({ type: 'reload', data: {} });

          // Trigger viewmodel reload animation
          const weaponCfg = WEAPONS[this.weaponState.id];
          this.viewmodel.startReloadAnimation(weaponCfg.type);

          // Cancel any active procedural inspect animation on reload start.
          this.inspectAnimation.onReload();

          // Force-exit scope on reload — not a user toggle
          if (this.isScoped) forceUnscope(this);
        }
      }

      // Footstep sound
      if (isMoving(this.localPlayer) && this.localPlayer.isGrounded) {
        this.footstepTimer += dt;
        const stepInterval = this.localPlayer.isCrouching ? 0.5 : 0.35;
        if (this.footstepTimer >= stepInterval) {
          const surface = detectSurface(this.scene, this.localPlayer.position);
          const mode = this.localPlayer.isCrouching
            ? 'creep'
            : inputState.walk
              ? 'walk'
              : 'sprint';
          playFootstep(surface, mode);
          this.footstepTimer = 0;
        }
      } else {
        this.footstepTimer = 0;
      }
    }

    // ── Match replay: record frame every tick ──
    if (this.state === 'playing' && this.replayRecorder.isRecording()) {
      const rpPlayers: import('./net/messages').PlayerSnapshot[] = [];
      rpPlayers.push({
        id: this.localPlayer.id,
        position: { x: this.localPlayer.position.x, y: this.localPlayer.position.y, z: this.localPlayer.position.z },
        yaw: this.localPlayer.yaw,
        pitch: this.localPlayer.pitch,
        health: this.localPlayer.health,
        armor: this.localPlayer.armor ?? 0,
        weapon: this.localPlayer.currentWeapon,
        isCrouching: this.localPlayer.isCrouching,
        weaponSkinId: this.weaponLoadout.equipped[this.localPlayer.currentWeapon] ?? 0,
        team: this.localPlayer.team,
      });
      for (const [, rp] of this.remotePlayers) {
        rpPlayers.push({
          id: rp.id,
          position: { x: rp.position.x, y: rp.position.y, z: rp.position.z },
          yaw: rp.yaw,
          pitch: rp.pitch,
          health: rp.health,
          armor: 0,
          weapon: rp.currentWeapon,
          weaponSkinId: rp.currentWeaponSkinId,
          isCrouching: rp.isCrouching,
        });
      }
      const snapshotInput: SnapshotInput = {
        tick: this.clock.getEstimatedServerTick(),
        players: rpPlayers,
        roundPhase: this.roundPhase,
        roundNumber: this.roundNum,
        teamScores: [0, this.tScore, this.ctScore],
      };
      this.replayRecorder.recordFrame(snapshotInput, []);
    }

    // Death replay update — plays slow-motion killer POV, then transitions to spectator
    if (this.state === 'dead') {
      const drState = this.deathReplay.getState();

      if (drState.phase === 'playing') {
        // Advance death replay timeline (camera positioned in render)
        this.deathReplay.update(dt);
      } else if (drState.phase === 'delay') {
        // Advance delay timer (camera positioned in render)
        this.deathReplay.update(dt);
      } else if (drState.phase === 'ended' || (drState.phase === 'idle' && this.killcamActive)) {
        // Death replay ended OR basic killcam fallback — manage killcam timer
        if (this.killcamActive) {
          this.killcamTimer += dt;
          if (this.killcamTimer >= this.KILLCAM_DURATION || drState.phase === 'ended') {
            this.killcamActive = false;
            this.killcamTarget = null;
            this.deathReplay.reset();
            this.state = 'spectating';
            // MOB-VIS: spectating is an in-match state — show touch overlays.
            this.touchInput?.setVisible(true);
            this.deathScreen.hide();
            this.spectator.enter(0, this.camera.camera.position.clone());
            this.spectatorHud.show();
            this.hud.setSpectatorMode(true);
            this.scoreboard.setSpectating(true);
            // H4: Auto-enable auto-director on spectator entry
            if (!this.autoDirector.isActive) this.autoDirector.toggle();
          }
        }
      }
    }

    // Death-to-spectator timer (when killed without killcam, e.g. suicide/fall)
    if (this.state === 'dead' && !this.killcamActive && !this.spectator.active && this.deathReplay.getState().phase === 'idle') {
      this.deathToSpectatorTimer += dt;
      if (this.deathToSpectatorTimer >= 3) {
        // Enter spectator mode
        this.state = 'spectating';
        // MOB-VIS: spectating is an in-match state — show touch overlays.
        this.touchInput?.setVisible(true);
        this.deathScreen.hide();
        this.spectator.enter(0, this.camera.camera.position.clone());
        this.spectatorHud.show();
        this.hud.setSpectatorMode(true);
        this.scoreboard.setSpectating(true);
        // H4: Auto-enable auto-director on spectator entry
        if (!this.autoDirector.isActive) this.autoDirector.toggle();
      }
    }

    // Wallbang timer
    if (this.wallbangTimer > 0) {
      this.wallbangTimer -= dt;
      if (this.wallbangTimer <= 0 && this.wallbangEl) {
        this.wallbangEl.style.display = 'none';
      }
    }

    // Ragdoll physics & weapon drop animations
    this.ragdollRenderer.update(dt);
    this.weaponDropMgr.update(dt);

    // Grenade projectile physics prediction
    this.grenadeProjectiles.update(dt);

    // Grenade trajectory preview — show arc when grenade is equipped.
    // G11: only render the preview in Practice mode. Live competitive /
    // wingman / casual / DM / arms-race players must aim by feel; the arc
    // is a training-mode aid only.
    const trajectoryAllowed = this.connection.getGameMode() === 'practice';
    if (trajectoryAllowed && this.selectedGrenade && this.grenadeThrowAnimTimer <= 0) {
      _tmpGrenadeEye.set(
        this.localPlayer.position.x,
        this.localPlayer.position.y + 1.62, // eye height = PlayerHeight(1.8) * 0.9, matches server
        this.localPlayer.position.z,
      );
      this.grenadeTrajectory.update(
        this.scene,
        _tmpGrenadeEye,
        this.localPlayer.yaw,
        this.localPlayer.pitch,
        0, // default to overhand; could track current throw mode
        this.selectedGrenade,
      );
    } else {
      this.grenadeTrajectory.hide();
    }

    // Update bomb 3D entity (blinking light animation)
    this.bombEntity.update(dt);

    // Bomb tick sound — play at intervals when bomb is planted and visible
    if (this.bombEntity.group.visible && this.bombEntity.isPlantedState()) {
      this.bombTickTimer -= dt;
      if (this.bombTickTimer <= 0) {
        // Urgent ticks (faster) in last 10 seconds
        const urgent = this.bombEntity.getTimeRemaining() < 10;
        playBombTick(urgent);
        this.bombTickTimer = urgent ? 0.4 : 1.0;
      }
    } else {
      this.bombTickTimer = 0;
    }

    // Update fire zones — remove expired ones
    this.activeFireZones = this.activeFireZones.filter((fz) => fz.update(dt));

    // Update smoke zones — remove expired ones
    this.activeSmokeZones = this.activeSmokeZones.filter((sz) => sz.isActive());

    // Flash blind timer
    if (this.flashBlindTimer > 0) {
      this.flashBlindTimer -= dt;
      if (this.flashBlindTimer < 0) this.flashBlindTimer = 0;
    }

    // Grenade throw animation timer — wait for viewmodel throw to finish
    if (this.grenadeThrowAnimTimer > 0) {
      this.grenadeThrowAnimTimer -= dt;
      // Switch weapon only after the throw animation finishes (release point passed)
      const animDone = !this.viewmodel.isGrenadeThrowActive();
      if (this.grenadeThrowAnimTimer <= 0 || animDone) {
        this.grenadeThrowAnimTimer = 0;
        // Auto-switch back to previous weapon after throw.
        // BUG-W-5: route through switchWeapon() helper so the server is told
        // (weapon_switch message) — otherwise remote players still see the
        // grenade in the local player's hands until the next snapshot reconcile.
        if (this.previousWeaponBeforeGrenade) {
          // BUG-MT-20: remember the slot before clearing so the mobile GRN
          // cycle can resume from here instead of restarting at index 0.
          if (this.selectedGrenade) {
            const GRN_CYCLE: GrenadeId[] = [
              'he_grenade',
              'flashbang',
              'smoke_grenade',
              'molotov',
              'incendiary',
              'decoy_grenade',
            ];
            const idx = GRN_CYCLE.indexOf(this.selectedGrenade);
            if (idx >= 0) this.lastNonEmptyGrenadeIndex = idx;
          }
          this.selectedGrenade = null;
          switchWeapon(this, this.previousWeaponBeforeGrenade);
          this.previousWeaponBeforeGrenade = null;
        }
      }
    }

    // Update HUD
    this.hud.updateHealth(this.localPlayer.health);
    this.hud.updateArmor(this.localPlayer.armor);
    if (this.selectedGrenade) {
      const grenadeCfg = GRENADES[this.selectedGrenade];
      this.hud.updateAmmo(this.grenadeInventory[this.selectedGrenade] ?? 0, 0);
      this.hud.updateWeaponName(grenadeCfg?.name ?? this.selectedGrenade);
    } else {
      this.hud.updateAmmo(this.weaponState.ammo, this.weaponState.reserveAmmo);
      this.hud.updateWeaponName(WEAPONS[this.weaponState.id].name);
    }
  }

  // ── Render (display refresh rate) ──
  private render(alpha: number): void {
    this.debug.tick();
    const now = performance.now();
    const dt = this.lastRenderTime > 0 ? Math.min((now - this.lastRenderTime) / 1000, 0.05) : 1 / 60;
    this.lastRenderTime = now;

    // G-H-002: feed the thermal-throttle detector this frame's elapsed time
    // in milliseconds. Null on desktop. The detector internally amortises
    // its sliding window and only emits a downgrade callback once per
    // throttle event, so this is cheap to call every frame.
    this.thermalThrottle?.recordFrameTime(dt * 1000);

    // BUG-MT-10: thermalThrottle ratchets the adaptive devicePixelRatio down
    // as sustained throttling is detected. Apply the new DPR to the renderer
    // + EffectComposer when it changes so the lower resolution actually takes
    // effect. Skip on desktop (thermalThrottle is null).
    if (this.thermalThrottle) {
      const dpr = this.thermalThrottle.getCurrentDpr();
      if (dpr !== this._lastDpr) {
        this.renderer.setPixelRatio(dpr);
        this.postProcessing?.setSize(window.innerWidth, window.innerHeight);
        this._lastDpr = dpr;
      }
    }

    if (this.state === 'playing' || this.state === 'dead' || this.state === 'spectating') {
      // BUG-HUD-15: the mobile control legend overlay is wired through
      // mobile-controls-hint.ts. The page-load path (main.ts) covers
      // first-paint, but a player can land in 'playing' without ever
      // refreshing — e.g. after returning from the death screen on first
      // launch. Fire the per-tick notifier on the very first frame the
      // game enters a gameplay state. The notifier short-circuits after
      // the first call (legendHandled flag inside the module), so the
      // local guard is just to avoid the import-call cost on subsequent
      // frames.
      if (!this._mobileGameplayTickFired && this.state === 'playing') {
        this._mobileGameplayTickFired = true;
        notifyMobileGameplayTick();
      }
      // Undo previous frame's shake
      undoScreenShake(this.camera.camera, this.shakeOffset);

      if (this.state === 'spectating' && this.spectator.active) {
        // Spectator camera
        this.spectator.update(this.camera, this.remotePlayers, dt);

        // Update spectator HUD
        const targetPlayer = this.remotePlayers.get(this.spectator.targetId);
        this.spectatorHud.updateTarget(targetPlayer?.name ?? 'None');
        this.spectatorHud.updateMode(this.spectator.mode);

        // Build player list for spectator HUD (cached — only rebuild when players/target change)
        {
          const curCount = this.remotePlayers.size;
          const curTarget = this.spectator.targetId;
          let needsRebuild = curCount !== this._lastSpectatorPlayerCount
            || curTarget !== this._lastSpectatorTargetId;
          // Also check if any player's alive state changed
          if (!needsRebuild && this._cachedSpectatorList) {
            let i = 0;
            for (const [, rp] of this.remotePlayers) {
              if (i < this._cachedSpectatorList.length && (rp.health > 0) !== this._cachedSpectatorList[i].alive) {
                needsRebuild = true;
                break;
              }
              i++;
            }
          }
          if (needsRebuild) {
            const playerList: Array<{ id: number; name: string; alive: boolean; team: number; isTarget: boolean }> = [];
            for (const [id, rp] of this.remotePlayers) {
              playerList.push({
                id,
                name: rp.name,
                alive: rp.health > 0,
                team: 0, // We don't track remote team client-side beyond scoreboard
                isTarget: id === curTarget,
              });
            }
            this._cachedSpectatorList = playerList;
            this._lastSpectatorPlayerCount = curCount;
            this._lastSpectatorTargetId = curTarget;
          }
          if (this._cachedSpectatorList) {
            this.spectatorHud.updatePlayerList(this._cachedSpectatorList);
          }
        }

        // X-ray overlay: render team-colored see-through materials
        if (this.xrayOverlay.isActive) {
          this.xrayOverlay.update(this.remotePlayers);
        }

        // Auto-director: automatically switch to most interesting player
        if (this.autoDirector.isActive) {
          // Prune stale events (older than 5 seconds)
          const now = Date.now();
          this.recentEvents = this.recentEvents.filter(
            (e) => now - (e.data.time ?? 0) < 5000,
          );

          const bombGroup = this.bombEntity.group;
          const bombPos = bombGroup.visible
            ? { x: bombGroup.position.x, z: bombGroup.position.z }
            : null;
          const newTarget = this.autoDirector.update(
            this.remotePlayers,
            this.recentEvents,
            bombGroup.visible,
            bombPos,
          );
          if (newTarget > 0) {
            this.spectator.setTarget(newTarget);
          }
        }
      } else if (this.state === 'dead' && this.killcamTarget && this.deathReplay.getState().phase !== 'idle') {
        // Death replay: third-person camera behind + above killer
        const killerPos = this.killcamTarget.position;
        const killerYaw = this.killcamTarget.yaw;
        // Position camera 3 units behind and 2.5 above the killer
        const behindX = Math.sin(killerYaw) * 3;
        const behindZ = Math.cos(killerYaw) * 3;
        _tmpCamDesired.set(
          killerPos.x + behindX,
          killerPos.y + 2.5,
          killerPos.z + behindZ,
        );
        this.camera.camera.position.lerp(_tmpCamDesired, 0.08);
        this.camera.camera.lookAt(killerPos.x, killerPos.y + 1.2, killerPos.z);
      } else if (this.killcamActive && this.killcamTarget && this.deathReplay.getState().phase === 'idle') {
        // Killcam: follow killer (fallback when death replay not active)
        const killerPos = this.killcamTarget.position;
        _tmpCamTarget.set(killerPos.x, killerPos.y + 2.5, killerPos.z); // above their head
        _tmpCamOffset.set(0, 1.5, 4);
        this.camera.camera.position.lerpVectors(
          this.camera.camera.position,
          _tmpCamTarget.add(_tmpCamOffset),
          0.05,
        );
        _tmpCamLookAt.set(killerPos.x, 1.2, killerPos.z);
        this.camera.camera.lookAt(_tmpCamLookAt);
      } else {
        // Normal: follow local player (use smoothed eye height for crouch/slide transitions)
        this.camera.update(
          this.localPlayer.position,
          undefined,
          undefined,
          getSmoothedEyeHeight(this.localPlayer),
          dt,
        );
      }

      // Update spatial audio listener to match camera
      const camPos = this.camera.camera.position;
      const camDir = this.camera.getDirection();
      updateAudioListener(camPos.x, camPos.y, camPos.z, camDir.x, camDir.y, camDir.z);

      // F-MAP-001: Vegetation wind animation + per-chunk LOD culling.
      updateVegetation(dt, camPos);

      // Smoke fog overlay — reduce visibility when camera is inside smoke cloud
      updateSmokeFog(camPos, dt);

      // Apply screen shake
      this.shakeOffset = applyScreenShake(this.camera.camera, dt);

      // Aimpunch (camera flinch on damage)
      updateAimpunch(dt);

      // Advance spring-based visual recoil (R1). Push offsets into camera +
      // viewmodel so they're layered into the composited transforms below.
      this.recoilKick.update(dt);
      const rk = this.recoilKick.getOffsets();
      this.camera.setVisualRecoilOffset(rk.cameraPitch, rk.cameraRoll);
      this.viewmodel.setVisualRecoilOffset(rk.viewmodelPosZ, rk.viewmodelRotX);

      // Advance procedural inspect animation (R3). When the manual inspector
      // is NOT active, push the offsets into viewmodel.group so they ride on
      // top of the per-frame transform composite.
      if (this.inspectAnimation.isPlaying()) {
        this.inspectAnimElapsed += dt;
      } else {
        this.inspectAnimElapsed = 0;
      }

      // Update viewmodel (only in FPP, not during killcam or spectating)
      const isFPP = this.camera.getMode() === 'fpp';
      const isSpectating = this.state === 'spectating';
      if (isFPP && !isSpectating) {
        this.viewmodel.update(dt, isMoving(this.localPlayer));
        this.viewmodel.syncCamera(this.camera.camera);

        // Procedural inspect offsets are applied as an outer-group overlay
        // after viewmodel.update so they don't fight the inner composite.
        if (this.inspectAnimation.isPlaying() && !this.viewmodel.inspector.isInspecting) {
          const insp = this.inspectAnimation.getAnimationOffset(this.inspectAnimElapsed);
          this.viewmodel.group.position.set(insp.positionX, insp.positionY, insp.positionZ);
          this.viewmodel.group.rotation.set(insp.rotationX, insp.rotationY, insp.rotationZ);
        } else {
          this.viewmodel.group.position.set(0, 0, 0);
          this.viewmodel.group.rotation.set(0, 0, 0);
        }
      }

      // Update local player body (follows player)
      if (this.localPlayerBody) {
        this.localPlayerBody.position.copy(this.localPlayer.position);
        // Preserve X rotation for TPP fire recoil, smoothly recover toward 0
        const currentTiltX = this.localPlayerBody.rotation.x;
        this.localPlayerBody.rotation.set(0, this.localPlayer.yaw, 0);
        if (!isFPP) {
          // Recover recoil tilt smoothly (lerp toward 0)
          this.localPlayerBody.rotation.x = currentTiltX * 0.9;
        }

        // When the local player is dead, hide the standing body so the ragdoll
        // renderer's cloned corpse is the only thing visible in spectator mode.
        const isDead = this.localPlayer.health <= 0;
        if (isDead) {
          if (this._lastBodyVisible !== false) {
            this.localPlayerBody.traverse(c => { c.visible = false; });
            this._lastBodyVisible = false;
          }
        } else if (isFPP) {
          // Hide entire body in FPP — viewmodel handles weapon rendering.
          // Only traverse on state change to avoid per-frame O(n) tree walk.
          if (this._lastBodyVisible !== false) {
            this.localPlayerBody.traverse(c => { c.visible = false; });
            this._lastBodyVisible = false;
          }
        } else {
          // Show body in TPP and scale for crouch/slide
          if (this._lastBodyVisible !== true) {
            this.localPlayerBody.traverse(c => { c.visible = true; });
            this._lastBodyVisible = true;
          }
          const targetScaleY = this.localPlayer.isSliding
            ? 0.5  // SLIDE_HEIGHT(0.9) / PLAYER_HEIGHT(1.8)
            : this.localPlayer.isCrouching
              ? PLAYER_CROUCH_HEIGHT / PLAYER_HEIGHT
              : 1.0;
          // Smooth the scale transition
          this.localPlayerBody.scale.y += (targetScaleY - this.localPlayerBody.scale.y) * 0.2;

          // Pitch the TPP weapon to follow the player's vertical aim so the
          // gun visibly tracks the crosshair instead of staying horizontal.
          setTPPWeaponPitch(this.localPlayerBody, this.localPlayer.pitch);
        }
      }

      // Interpolate remote players
      for (const [, remote] of this.remotePlayers) {
        remote.interpolate(alpha);
      }

      // Update damage numbers
      this.damageNumbers.update(dt, this.camera.camera);

      // Update directional indicators
      this.directionalIndicator.update(dt);
      this.damageIndicator.update(dt);

      // Update kill/headshot effect animations
      this.killEffect.update(dt);
      this.headshotIndicator.update(dt);

      // Update minimap
      const remoteData: Array<{ x: number; z: number; alive: boolean }> = [];
      for (const [, rp] of this.remotePlayers) {
        remoteData.push({
          x: rp.position.x,
          z: rp.position.z,
          alive: rp.health > 0,
        });
      }
      // Build bomb state for minimap (planted bomb icon)
      const bombGroup = this.bombEntity.group;
      const minimapBomb = bombGroup.visible
        ? { planted: true, x: bombGroup.position.x, z: bombGroup.position.z }
        : undefined;

      this.minimap.update(
        { x: this.localPlayer.position.x, z: this.localPlayer.position.z },
        this.localPlayer.yaw,
        remoteData,
        minimapBomb,
        dt,
      );

      // Debug info
      this.hud.updateDebugInfo(
        this.debug.getFPS(),
        Math.round(this.connection.getPing()),
        TICK_RATE,
      );
    }

    // Update visibility culling and prop LODs
    if (this.camera) {
      updatePVS(this.camera.camera);
      updatePropLODs(this.camera.camera);

      // Phase 10: Process texture streaming queue (nearest first)
      const streamer = getSceneTextureStreamer();
      if (streamer && !streamer.isComplete()) {
        streamer.processQueue(this.camera.camera.position);
      }
    }

    // Update ambient audio crossfades
    ambientAudio.update(dt);

    // Reset audio occlusion raycast budget for this frame
    this.audioOcclusion.resetFrameBudget();

    // Update menu background camera orbit (no-op when inactive)
    this.menuBackground.update(dt);

    // Update atmospheric effects
    if (this.atmosphere && this.camera) {
      this.atmosphere.update(dt, this.camera.camera.position);
    }
    if (this.puddleSystem && this.camera) {
      this.puddleSystem.update(this.camera.camera);
    }

    // Volumetric smoke clouds — expand/fade animation + per-frame uniforms
    if (this.volumetricSmoke && this.camera) {
      this.volumetricSmoke.update(dt, this.camera.camera.position);
    }

    // Shell casings — physics + fade, compacted into a single InstancedMesh
    if (this.shellCasings) {
      this.shellCasings.update(now);
    }

    // Render world via post-processing pipeline
    if (this.renderer && this.scene && this.camera) {
      updateCSM(); // must run before render — repositions cascade shadow cameras
      this.postProcessing.render();
      // Two-pass: render viewmodel on top (clears depth only) — FPP only, not spectating
      if (this.state === 'playing' && !this.killcamActive && this.camera.getMode() === 'fpp') {
        this.viewmodel.render(this.renderer);
      }
    }

    // Dynamic crosshair rendering
    if (this.hud && this.weaponState) {
      const spread = getCurrentSpread(
        this.weaponState,
        isMoving(this.localPlayer),
        this.localPlayer.isCrouching,
        performance.now(),
      );
      this.hud.setCrosshairSpread(spread);
      this.hud.renderCrosshair();
    }

    const netGraphData = this.netGraph.getDataCollector();
    const connectionQuality = this.connection.getConnectionQuality();
    netGraphData.recordFps(this.debug.getFPS());
    netGraphData.recordPing(connectionQuality.rtt);
    netGraphData.recordLoss(connectionQuality.packetLoss);
    netGraphData.recordChoke(connectionQuality.choke);
    netGraphData.recordInterp(this.interpolator.getInterpolationDelay());
    this.netGraph.update();
  }

  // ── Player-vs-player collision (client prediction) ──
  private resolveLocalPlayerCollision(): void {
    const lp = this.localPlayer;
    if (!lp.alive) return;
    const minDist = PLAYER_RADIUS * 2;
    for (const rp of this.remotePlayers.values()) {
      if (rp.health <= 0) continue;
      const dx = lp.position.x - rp.position.x;
      const dz = lp.position.z - rp.position.z;
      const distSq = dx * dx + dz * dz;
      if (distSq >= minDist * minDist || distSq < 0.0001) continue;
      // Check Y overlap (standing height)
      const lpTop = lp.position.y + (lp.isCrouching ? PLAYER_CROUCH_HEIGHT : PLAYER_HEIGHT);
      const rpTop = rp.position.y + PLAYER_HEIGHT;
      if (lp.position.y >= rpTop || rp.position.y >= lpTop) continue;
      const dist = Math.sqrt(distSq);
      const overlap = minDist - dist;
      const nx = dx / dist;
      const nz = dz / dist;
      lp.position.x += nx * overlap * 0.5;
      lp.position.z += nz * overlap * 0.5;
    }
  }

  private exitReplayMode(): void {
    if (this.replayRenderer) {
      this.replayRenderer.dispose();
      this.replayRenderer = null;
    }
    if (this.replayUI) {
      this.replayUI.dispose();
      this.replayUI = null;
    }
    if (this.replayPlayer) {
      this.replayPlayer.stop();
      this.replayPlayer = null;
    }
    this.spectator.exit();
    this.state = 'menu';
    // MOB-VIS: returning to menu from replay — hide touch overlays.
    this.touchInput?.setVisible(false);
    this.menuBackground.start(this.camera.camera);
    this.mainMenu.show();
    this.skinSelector.show();
  }

  /**
   * BUG-PARITY-H4: Send a `surrender_start` message to the server. The server
   * validates competitive mode + min round (15) and emits `surrender_vote_started`
   * if accepted. Exposed as a public method so a future scoreboard / settings-menu
   * button can call it; no keybind is wired yet (avoids cross-agent risk on
   * keybinds.ts).
   */
  initiateSurrenderVote(): void {
    if (this.state !== 'playing' && this.state !== 'dead' && this.state !== 'spectating') return;
    this.connection.send({ type: 'surrender_start', data: {} });
  }

  /**
   * MT-7 polish: mobile VOTE button entry point. If the panel is already
   * open we just close it (no fetch needed). Otherwise we probe the
   * server's map-vote state and either open the panel (if active) or
   * surface a HUD kill-feed-style toast ("Map vote not active") so the
   * player doesn't see an empty panel. Fetch failures fall back to the
   * raw toggle() — the panel itself renders its own error state.
   */
  private async toggleMapVoteWithGuard(): Promise<void> {
    if (this.mapVotePanel.isOpen()) {
      this.mapVotePanel.toggle();
      return;
    }
    const httpBase = this.serverUrl
      .replace(/^wss:/, 'https:')
      .replace(/^ws:/, 'http:')
      .replace(/\/ws$/, '')
      .replace(/\/$/, '');
    try {
      const res = await fetch(`${httpBase}/api/map-vote`);
      if (!res.ok) {
        this.mapVotePanel.toggle();
        return;
      }
      const state = (await res.json()) as { active?: boolean };
      if (!state.active) {
        this.hud.addEventFeedEntry('VOTE', 'Map vote not active', '#888888');
        return;
      }
      this.mapVotePanel.toggle();
    } catch {
      // Network failure: fall back to the raw toggle so the panel can show its own error.
      this.mapVotePanel.toggle();
    }
  }

  private resetDisconnectedMatchState(): void {
    if (this.replayRecorder.isRecording()) {
      this.replayRecorder.stopRecording();
    }

    for (const [id, remotePlayer] of this.remotePlayers) {
      remotePlayer.removeMesh(this.scene);
      this.interpolator.removePlayer(id);
    }
    this.remotePlayers.clear();
    this._cachedSpectatorList = null;
    this._lastSpectatorPlayerCount = -1;
    this._lastSpectatorTargetId = -1;

    this.tScore = 0;
    this.ctScore = 0;
    this.roundNum = 1;
    this.roundPhase = 'active';
    this.roundScore.setPhase('active');
    this.roundScore.update(this.tScore, this.ctScore, this.roundNum, 30);

    this.money = 100000;
    this.localPlayer.health = 100;
    this.localPlayer.armor = 0;
    this.localPlayer.team = 0;
    this.localPlayer.currentWeapon = 'glock';
    this.localPlayer.ammo = WEAPONS.glock.magazine;
    this.localPlayer.reserveAmmo = WEAPONS.glock.reserveAmmo;

    this.weaponState = createWeaponState('glock');
    resetLoadoutForTeam(this.loadoutSlots, this.localPlayer.team);
    this.selectedGrenade = null;
    this.previousWeaponBeforeGrenade = null;
    this.grenadeThrowAnimTimer = 0;
    this.lastNonEmptyGrenadeIndex = -1;
    this.grenadeInventory = createEmptyInventory();
    this.hud.updateGrenadeSelection(null, this.grenadeInventory);

    this.killcamActive = false;
    this.killcamTarget = null;
    this.killcamTimer = 0;
    this.deathToSpectatorTimer = 0;
    this.deathReplay.reset();
    this.recentEvents = [];

    this.deltaDecoder.reset();
    this.lastExtendedState = null;

    this.weaponDropMgr.clear();
    this.ragdollRenderer.clear();
    for (const smokeZone of this.activeSmokeZones) smokeZone.remove();
    for (const fireZone of this.activeFireZones) fireZone.remove();
    this.activeSmokeZones = [];
    this.activeFireZones = [];
    this.bombEntity.group.visible = false;

    if (this.xrayOverlay.isActive) {
      this.xrayOverlay.toggle();
    }
    if (this.autoDirector.isActive) {
      this.autoDirector.toggle();
    }
    this.autoDirector.reset();
  }

  dispose(): void {
    this.loop.stop();
    input.destroy();
    if (this._onResize) window.removeEventListener('resize', this._onResize);
    if (this._onSettingsFovChange) window.removeEventListener('settings-fov-change', this._onSettingsFovChange);
    if (this._onKeyDown) document.removeEventListener('keydown', this._onKeyDown);
    if (this._onKeyUp) document.removeEventListener('keyup', this._onKeyUp);
    if (this._onMouseDown) document.removeEventListener('mousedown', this._onMouseDown);
    if (this._onContextMenu) document.removeEventListener('contextmenu', this._onContextMenu);
    // BUG-PARITY-H4: abort the surrender-vote-initiate listener (registered with signal).
    if (this._surrenderVoteAbort) {
      this._surrenderVoteAbort.abort();
      this._surrenderVoteAbort = null;
    }
    cleanupNetworkTimers();
    // F-RN-012: clear colorblind body class on shutdown so a hot reload doesn't inherit a stale filter.
    void import('./renderer/colorblind').then((m) => m.removeColorblindCSS()).catch(() => {});
    this.camera.dispose();
    this.viewmodel.dispose();
    disposePools();
    disposeEnhancedMuzzle();
    disposeScene();
    disposeNavMesh(this.scene);
    audioManager.dispose();
    musicSystem.dispose();
    ambientAudio.dispose();
    this.audioOcclusion.dispose();
    this.weaponSkinLoader?.dispose();
    voiceInput.disable();
    voiceChat.dispose();
    this.connection.disconnect();
    this.postProcessing.dispose();
    this.atmosphere.dispose();
    this.puddleSystem.dispose();
    this.menuBackground.dispose();

    disposeProps();
    this.minimap.dispose();
    this.damageNumbers.dispose();
    this.scopeOverlay.dispose();
    this.directionalIndicator.dispose();
    this.damageIndicator.dispose();
    this.buyMenu.dispose();
    this.roundScore.dispose();
    this.skinLoader.dispose();
    this.loadingScreen.dispose();
    this.skinSelector.dispose();
    this.skinMarket?.dispose();
    this.skinPreview?.dispose();
    this.settingsMenu.dispose();
    this.spectator.dispose();
    this.spectatorHud.dispose();
    this.netGraph.dispose();
    this.xrayOverlay.dispose();
    this.ragdollRenderer.dispose();
    this.weaponDropMgr.dispose();
    this.killEffect.dispose();
    this.headshotIndicator.dispose();
    this.voiceIndicators.dispose();
    if (this.touchInput) this.touchInput.dispose();
    if (this.pingWheel) this.pingWheel.dispose();
    if (this.aimAssist) this.aimAssist.dispose();
    if (this.rotateOverlay) this.rotateOverlay.dispose();
    if (this.mobileTutorial) this.mobileTutorial.dispose();
    if (this.audioUnlock) this.audioUnlock.dispose();
    this.teamSelectMenu.dispose();
    this.registrationDialog.dispose();
    this.leaderboardPanel.dispose();
    this.socialPanel.dispose();
    this.mapVotePanel.dispose();
    this.clanPanel.dispose();
    this.tournamentPanel.dispose();
    this.casePanel?.dispose();
    this.votePanel.dispose();
    this.overwatchPanel.dispose();
    this.progressionPanel.dispose();
    this.replayMenu.dispose();
    this.serverBrowser?.destroy();
    this.deathReplay.reset();
    if (this.replayRenderer) this.replayRenderer.dispose();
    if (this.replayUI) this.replayUI.dispose();
    this.chatUI.dispose();
    this.chatComposer?.dispose();
    this.bombEntity.dispose();
    this.disposeBombSiteMarkers?.();
    this.grenadeProjectiles.dispose(this.scene);
    this.grenadeTrajectory.dispose();
    this.hud.dispose();
    for (const sz of this.activeSmokeZones) sz.remove();
    for (const fz of this.activeFireZones) fz.remove();
    disposeSmokeFog();
    disposeInteractionPrompt();
    settingsStore.offChange(this.onSettingsChange);
    disposeEffectTextureCache();
    if (this.volumetricSmoke) {
      this.volumetricSmoke.dispose();
      this.volumetricSmoke = null;
    }
    if (this.shellCasings) {
      this.shellCasings.dispose();
    }
    disposeBloodPool();
    disposeTracerPool();
    disposeEffectPools();
    disposeDecals();
  }
}
